import { cardAmountDue, cents, tryParseMoney } from '@sitzmark/core';
import { sanitiseAmount } from './amount.js';
import {
  ReturnToCardPanel,
  TerminalCardPanel,
  holdsSale,
  useCardAttempt,
  useRegisterCards,
} from './card-terminal.js';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Fragment, useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Button,
  Callout,
  Card,
  Explain,
  Modal,
  Mono,
  More,
  Screen,
} from '../../components/ui.js';
import { api, errorMessage } from '../../lib/api.js';
import { recordBreadcrumb } from '../../lib/diagnostics.js';
import { money } from '../../lib/money.js';
import { TENDER_LABELS } from '../../lib/tender.js';
import { useTypingFocus } from '../../lib/typing-focus.js';
import { approvalRefusal, isOverridable, ManagerApproval } from '../auth/manager-approval.js';
import type { ManagerOverride } from '../auth/manager-approval.js';
import { CustomerDetail, CustomerOnSale, CustomerPrompt } from '../customers/attach-customer.js';
import { customerName } from '../customers/customer.js';
import { ReceiptDocument, useReceiptPrinter, useReceiptPrinting } from '../sales/receipt.js';
import { EmailReceiptDialog } from '../sales/sales-screen.js';
import { LineAdjustDialog, SaleDiscountDialog } from './line-adjust.js';
import { BrowseDialog, StyleGridPicker } from './style-grid-picker.js';
import './register.css';

/**
 * The register (SCOPE §8).
 *
 * One screen: cart, customer, discounts and tender, with nothing to navigate
 * between. Search sits on top and keeps focus, so a scan lands in it from
 * anywhere — the scanner is a keyboard, and the fastest register is one where
 * hands never leave it (§2-1).
 *
 * **The server owns the current cart.** This screen does not remember a sale id
 * across mounts, because local memory is lost on navigation and — far worse —
 * can go stale and point at a sale that has since been completed or cancelled.
 * It asks `register.currentSale` every time, uncached. That single decision is
 * what keeps a finished customer's items from reappearing for the next one.
 */

type SaleView = NonNullable<Awaited<ReturnType<typeof api.register.currentSale.query>>>;

export interface RegisterContext {
  readonly registerId: string | null;
  readonly saleId: string | null;
  /**
   * The printed number (`S-100042`), for a feedback report's `recordRef`. A
   * report is read by a person, and a UUID where a number belongs makes every
   * "which sale?" a database query (STATUS §19).
   *
   * **Null for the common case**: a sale is numbered when it completes, not
   * when the cart opens — a discarded empty cart must not burn a receipt
   * number (decision 84). Mid-sale, `registerName` is the reference a person
   * can act on.
   */
  readonly saleNumber: string | null;
  /** `Register 1` — the name a triager can walk to. */
  readonly registerName: string | null;
}

/** Register search asks for the API's most; past that, it says so. */
const SEARCH_LIMIT = 50;

/** A typed gift card number is looked up after this long without a key, or on Enter. */
const GIFT_CARD_PAUSE_MS = 1500;

export function RegisterScreen({
  onContext,
  canSetUpTills = false,
}: {
  onContext?: (context: RegisterContext) => void;
  /** Admins may say which register this till is, from the register itself. */
  canSetUpTills?: boolean;
}) {
  const queryClient = useQueryClient();
  const [receipt, setReceipt] = useState<{
    number: string;
    changeCents: number;
    kind: string;
    saleId: string;
  } | null>(null);

  const stations = useQuery({
    queryKey: ['register', 'stations'],
    queryFn: () => api.register.stations.query(),
  });

  // The register this till is set to — never simply the first one, which put
  // every till in a shop on one drawer (2026-09-25).
  const station =
    stations.data?.registers.find((register) => register.id === stations.data.till.registerId) ??
    null;
  const drawer =
    stations.data?.drawers.find((entry) => entry.registerId === station?.id)?.drawer ?? null;

  const saleKey = ['register', 'currentSale', station?.id] as const;

  const current = useQuery({
    queryKey: saleKey,
    queryFn: () => api.register.currentSale.query({ registerId: station!.id }),
    enabled: station !== null && drawer !== null && receipt === null,
    // Always ask. A cached answer here is how a cancelled or completed sale
    // gets resurrected on the next customer.
    staleTime: 0,
    gcTime: 0,
  });

  const openSale = useMutation({
    mutationFn: (registerId: string) => api.register.openSale.mutate({ registerId }),
    onSuccess: (view) => queryClient.setQueryData(saleKey, view),
  });

  // Smart default (§2.2-5): with a drawer open and no cart in progress, the
  // register opens one. Nobody presses "new sale" to start a sale.
  useEffect(() => {
    if (
      station !== null &&
      drawer !== null &&
      receipt === null &&
      current.isSuccess &&
      current.data === null &&
      !openSale.isPending &&
      !openSale.isError
    ) {
      openSale.mutate(station.id);
    }
  }, [station, drawer, receipt, current.isSuccess, current.data, openSale]);

  const sale = current.data ?? null;

  useEffect(() => {
    onContext?.({
      registerId: station?.id ?? null,
      saleId: sale?.sale.id ?? null,
      saleNumber: sale?.sale.number ?? null,
      registerName: station?.name ?? null,
    });
  }, [onContext, station, sale]);

  if (stations.isPending) return <div className="boot">Opening the register…</div>;

  if (station === null) {
    if ((stations.data?.registers.length ?? 0) === 0) {
      return (
        <Screen title="Register">
          <Callout tone="warning">No register is configured for this store yet.</Callout>
        </Screen>
      );
    }
    return (
      <ChooseTillRegister
        registers={stations.data!.registers}
        canChoose={canSetUpTills}
        onChosen={() => void stations.refetch()}
      />
    );
  }

  if (drawer === null) {
    return <OpenDrawerScreen registerId={station.id} onOpened={() => void stations.refetch()} />;
  }

  if (receipt !== null) {
    return (
      <CompletedScreen
        receipt={receipt}
        onNext={() => {
          // Clear the cached cart *and* the receipt, so the next render starts
          // a genuinely new sale rather than re-reading the finished one.
          queryClient.setQueryData(saleKey, null);
          setReceipt(null);
        }}
      />
    );
  }

  if (openSale.isError) {
    return (
      <Screen
        title="Register"
        primaryAction={
          <Button variant="primary" onClick={() => openSale.mutate(station.id)}>
            Try again
          </Button>
        }
      >
        <Callout tone="danger" title="Could not start a sale">
          {errorMessage(openSale.error)}
        </Callout>
      </Screen>
    );
  }

  if (sale === null) return <div className="boot">Starting a sale…</div>;

  return (
    <Sale
      sale={sale}
      registerId={station.id}
      // Per-tenant config (§18.1-4). The client knows it only so a dialog can
      // warn that a manager will be needed before the button is pressed; the
      // server is still the one that decides (§2.2-7).
      thresholdPpm={stations.data?.priceOverrideThresholdPpm ?? 0}
      onSaleChanged={(view) => queryClient.setQueryData(saleKey, view)}
      onSaleCleared={() => queryClient.setQueryData(saleKey, null)}
      onCompleted={(number, changeCents, kind) =>
        setReceipt({ number, changeCents, kind, saleId: sale.sale.id })
      }
    />
  );
}

// ---------------------------------------------------------------------------

/**
 * A till that has not been told which register it is (2026-09-25).
 *
 * It asks rather than guesses: guessing is what put every till on one drawer.
 * An admin answers here, once; anyone else is told who can.
 */
function ChooseTillRegister({
  registers,
  canChoose,
  onChosen,
}: {
  registers: readonly { id: string; name: string }[];
  canChoose: boolean;
  onChosen: () => void;
}) {
  const [registerId, setRegisterId] = useState('');
  const choose = useMutation({
    mutationFn: () => api.registers.setThisTill.mutate({ registerId }),
    onSuccess: () => {
      recordBreadcrumb('register', 'set this device to a register');
      onChosen();
    },
  });

  return (
    <Screen
      title="Which register is this device?"
      subtitle="Each device rings on its own register, with its own drawer. It is asked once."
      primaryAction={
        canChoose ? (
          <Button
            variant="primary"
            size="large"
            busy={choose.isPending}
            disabledReason={registerId === '' ? 'Pick the register' : undefined}
            onClick={() => choose.mutate()}
          >
            Use this register
          </Button>
        ) : undefined
      }
    >
      {canChoose ? (
        <Card>
          <div className="field">
            <label className="field__label" htmlFor="till-register">
              This device is
            </label>
            <select
              id="till-register"
              className="select"
              value={registerId}
              autoFocus
              onChange={(event) => setRegisterId(event.target.value)}
            >
              <option value="">Pick a register</option>
              {registers.map((register) => (
                <option key={register.id} value={register.id}>
                  {register.name}
                </option>
              ))}
            </select>
            <p className="field__hint">
              A register another device was using moves to this one. Settings, under Devices, shows
              which device is on which.
            </p>
          </div>
        </Card>
      ) : (
        <Callout tone="warning" title="This device is not set to a register yet">
          An admin sets it once — here, when they are signed in, or in Settings under Devices. Until
          then this device cannot take a sale.
        </Callout>
      )}
      {choose.isError ? <Callout tone="danger">{errorMessage(choose.error)}</Callout> : null}
    </Screen>
  );
}

// ---------------------------------------------------------------------------

function OpenDrawerScreen({ registerId, onOpened }: { registerId: string; onOpened: () => void }) {
  const [float, setFloat] = useState('200.00');
  const parsed = tryParseMoney(float);

  const open = useMutation({
    mutationFn: () =>
      api.register.openDrawer.mutate({ registerId, openingFloatCents: parsed ?? 0 }),
    onSuccess: onOpened,
  });

  return (
    <Screen
      title="Open the drawer"
      subtitle="Count the starting float before the first sale."
      primaryAction={
        <Button
          variant="primary"
          size="large"
          busy={open.isPending}
          disabledReason={parsed === null ? 'Enter the starting float' : undefined}
          onClick={() => open.mutate()}
        >
          Open drawer
        </Button>
      }
    >
      <Card title="Starting float">
        <input
          className="input money"
          value={float}
          inputMode="decimal"
          autoFocus
          onChange={(event) => setFloat(event.target.value)}
        />
      </Card>
      {open.isError ? <Callout tone="danger">{errorMessage(open.error)}</Callout> : null}
    </Screen>
  );
}

// ---------------------------------------------------------------------------

function Sale({
  sale,
  registerId,
  thresholdPpm,
  onSaleChanged,
  onSaleCleared,
  onCompleted,
}: {
  sale: SaleView;
  registerId: string;
  thresholdPpm: number;
  onSaleChanged: (sale: SaleView) => void;
  onSaleCleared: () => void;
  onCompleted: (number: string, changeCents: number, kind: string) => void;
}) {
  const queryClient = useQueryClient();
  /**
   * How the customer is paying, while a payment is open; null on the cart.
   *
   * One value, held here and handed to the panel, never copied into it. The
   * panel kept its own copy of the method it opened with, so pressing *Pay
   * card* after *Pay cash* changed a value it never read again (FB-1121).
   */
  const [tendering, setTendering] = useState<TenderMethod | null>(null);
  /**
   * Card payments through BlueSage (decision 229): each press of *Pay card*
   * on the bar sends the balance to this register's default terminal at once.
   */
  const cards = useRegisterCards();
  const [cardPress, setCardPress] = useState(false);
  const cardAttempt = useCardAttempt(sale.sale.id, cards.enabled);
  /*
   * A payment still on a terminal — the screen was reloaded, or this register
   * picked the sale back up — opens the payment where it is (criterion 11).
   */
  useEffect(() => {
    if (tendering === null && holdsSale(cardAttempt.data)) setTendering('card');
  }, [tendering, cardAttempt.data]);
  const [dialog, setDialog] = useState<
    | 'hold'
    | 'holds'
    | 'cancel'
    | 'refund'
    | 'saleDiscount'
    | 'more'
    | 'giftCard'
    | 'layaway'
    | 'drawer'
    | 'closeDrawer'
    | null
  >(null);

  /**
   * "Ask for a customer" (SCOPE §9.1), when the store has turned it on.
   *
   * It fires on the way to payment rather than at the start of the sale,
   * because that is the moment the customer is already reaching for their card
   * and the cashier is waiting — dead time the shop already owns. Asking at
   * the first scan costs a step while the queue is still moving.
   *
   * **Ask, never block.** Skipping is one tap and the sale carries on; the ask
   * does not come back for this sale, because a prompt that reappears is a
   * prompt staff learn to dismiss without reading. Held once per sale, in
   * component state, so resuming a held sale asks again — the person at the
   * counter may be different by then.
   */
  const settings = useReceiptPrinting();
  const [askedForCustomer, setAskedForCustomer] = useState<string | null>(null);
  const [promptingCustomer, setPromptingCustomer] = useState<'cash' | 'card' | null>(null);

  const shouldAskForCustomer =
    settings?.registerPolicy.promptForCustomer === true &&
    sale.customer === null &&
    sale.sale.originalSaleId === null &&
    askedForCustomer !== sale.sale.id;

  /** Leave the payment for the cart; anything already taken stays taken. */
  const backToCart = () => {
    setTendering(null);
    focusSearch();
  };

  /** Every route to payment goes through here, so the ask cannot be bypassed. */
  const goToPayment = (kind: 'cash' | 'card') => {
    if (shouldAskForCustomer) {
      setPromptingCustomer(kind);
      return;
    }
    if (kind === 'card') setCardPress(true);
    setTendering(kind);
  };

  /**
   * Is this sale actually posting two prices?
   *
   * The sale's own snapshot decides, not the tenant setting — a cart opened
   * before the programme was switched on keeps the rate it was rung up under,
   * and the buttons have to agree with what will be charged.
   */
  const cardTotalCents = sale.sale.cardTotalCents;
  const dualPricing = cardTotalCents != null && cardTotalCents !== sale.sale.totalCents;
  /** Which cart line is being adjusted, if any. */
  const [adjustingId, setAdjustingId] = useState<string | null>(null);
  /**
   * Change owed on a layaway deposit, once it has been taken.
   *
   * Its own notice rather than the completion screen's: the sale did not
   * complete — it became a layaway — but cash still has to come back out of the
   * drawer, and money going the wrong way is exactly what must not be silent.
   */
  const [layawayChange, setLayawayChange] = useState<number | null>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const focusSearch = () => searchRef.current?.focus();

  /**
   * Focus comes back to the search box whenever a dialog closes (revision
   * item 2, §2.2 "return focus deliberately after every dialog closes").
   *
   * There are fourteen places that set `dialog` back to null — Escape, the
   * backdrop, a Cancel button, and every success path — and remembering
   * `focusSearch()` at each of them is exactly the kind of rule that is right
   * on the day it is written and wrong the first time somebody adds a
   * fifteenth. So it is stated once, as a consequence of the dialog closing,
   * and a new dialog gets it without knowing it exists.
   *
   * **An effect rather than a call inside the handlers**, because the `<dialog>`
   * still holds a focus trap when the handler runs: focusing the input before
   * React has committed the unmount puts focus straight back where it was. This
   * runs after the commit, when there is nothing left to trap it.
   *
   * The document-level `useTypingFocus` backstop already routes a *scanner*
   * burst here whatever has focus, so this is no longer load-bearing for a
   * scan. It is load-bearing for the operator who types a SKU by hand, and for
   * not making them click the box first.
   */
  const dialogWasOpen = useRef(false);
  useEffect(() => {
    if (dialog === null && dialogWasOpen.current) focusSearch();
    dialogWasOpen.current = dialog !== null;
  }, [dialog]);

  const holds = useQuery({
    queryKey: ['register', 'holds'],
    queryFn: () => api.register.holds.query(),
    staleTime: 5_000,
  });

  const addItem = useMutation({
    mutationFn: (variantId: string) =>
      api.register.addItem.mutate({ saleId: sale.sale.id, variantId }),
    onSuccess: (view) => {
      recordBreadcrumb('register', 'added an item to the cart');
      onSaleChanged(view);
      focusSearch();
    },
    onError: (error) => recordBreadcrumb('register', `add item failed: ${errorMessage(error)}`),
  });

  /** A consigned item, by its label (decision 131). */
  const addConsignment = useMutation({
    mutationFn: (itemId: string) =>
      api.register.addConsignment.mutate({ saleId: sale.sale.id, itemId }),
    onSuccess: (view) => {
      recordBreadcrumb('register', 'added a consigned item to the cart');
      onSaleChanged(view);
      focusSearch();
    },
    onError: (error) =>
      recordBreadcrumb('register', `add consignment failed: ${errorMessage(error)}`),
  });

  const setQuantity = useMutation({
    mutationFn: (input: { lineId: string; quantity: number }) =>
      api.register.setLineQuantity.mutate({ saleId: sale.sale.id, ...input }),
    onSuccess: (view) => {
      onSaleChanged(view);
      focusSearch();
    },
  });

  const hold = useMutation({
    mutationFn: (name: string) => api.register.hold.mutate({ saleId: sale.sale.id, name }),
    onSuccess: () => {
      recordBreadcrumb('register', 'held the cart');
      setDialog(null);
      onSaleCleared();
      void queryClient.invalidateQueries({ queryKey: ['register', 'holds'] });
    },
  });

  const resume = useMutation({
    mutationFn: (saleId: string) => api.register.resume.mutate({ saleId, registerId }),
    onSuccess: (view) => {
      setDialog(null);
      onSaleChanged(view);
      void queryClient.invalidateQueries({ queryKey: ['register', 'holds'] });
      focusSearch();
    },
  });

  const cancelSale = useMutation({
    mutationFn: () =>
      api.register.void.mutate({ saleId: sale.sale.id, reason: 'Canceled at register' }),
    onSuccess: () => {
      recordBreadcrumb('register', 'canceled the cart');
      setDialog(null);
      onSaleCleared();
    },
  });

  const refundTender = useMutation({
    mutationFn: (input: { tenderId: string; reason: string }) =>
      api.register.refundTender.mutate({ saleId: sale.sale.id, ...input }),
    onSuccess: (view) => {
      recordBreadcrumb('register', 'refunded a payment');
      onSaleChanged(view);
    },
  });

  const isEmpty = sale.lines.length === 0;
  /* Looked up from the live sale, never held as a copy: the dialog has to see
     the line as the server last returned it, or a second adjustment is computed
     against a stale price. Same reasoning as the cart itself. */
  const adjustingLine = sale.lines.find((line) => line.id === adjustingId);
  const heldCount = holds.data?.length ?? 0;
  /** A refund reads and behaves differently: money goes out, nothing is scanned. */
  const isRefund = sale.sale.originalSaleId !== null;

  const livePayments = sale.tenders.filter((tender) => tender.voidedAt === null);
  /*
   * An exchange credit is not money anyone paid (§8.5).
   *
   * Counting it as one made the register lie in three places at once: it
   * offered to "refund" the credit, it locked the cart behind "refund the
   * payment taken so far", and it announced an amount as already paid that
   * never came out of anybody's pocket. It is the other half of this
   * transaction, so every question of the form "has money changed hands?"
   * has to be asked of the real tenders only.
   */
  const realPayments = livePayments.filter((tender) => tender.kind !== 'exchange_credit');
  const paidCents = realPayments.reduce(
    (total, tender) => total + tender.amountCents - tender.changeGivenCents,
    0,
  );
  const partPaid = realPayments.length > 0;
  const lockedReasonId = useId();

  /*
   * What is *still owed*, which is not the sale total the moment anything has
   * been applied to it — a credit, a deposit, half the money.
   *
   * Summed from `cashEquivalentCents` rather than from what was collected,
   * because that is the field that measures basket rather than money: a card
   * payment collects more than it covers, and netting the collected figure
   * against the total reports a dual-priced sale as overpaid. This is the same
   * arithmetic `loadSettlement` does on the server, so the button and the
   * payment screen can no longer disagree.
   */
  const settledCents = livePayments.reduce(
    (total, tender) => total + tender.cashEquivalentCents,
    0,
  );
  const outstandingCashCents = sale.sale.totalCents - settledCents;
  const outstandingCardCents =
    cardTotalCents == null || sale.sale.totalCents === 0
      ? outstandingCashCents
      : cardAmountDue(
          cents(outstandingCashCents),
          cents(sale.sale.totalCents),
          cents(cardTotalCents),
        );

  // --- exchanges (SCOPE §8.5) ----------------------------------------------
  /**
   * An exchange is a refund and an ordinary sale, joined and settled once.
   * Both halves carry the group; the refund is the one with an original.
   */
  const inExchange = sale.sale.exchangeGroupId !== null;

  const exchange = useQuery({
    queryKey: ['register', 'exchange', sale.sale.id],
    enabled: inExchange,
    queryFn: () => api.register.exchange.query({ saleId: sale.sale.id }),
    staleTime: 0,
  });

  /** Has the pair already been netted? Then the credit is a live tender. */
  const creditApplied = livePayments.some((entry) => entry.kind === 'exchange_credit');

  const startExchange = useMutation({
    mutationFn: () => api.register.startExchange.mutate({ registerId, refundSaleId: sale.sale.id }),
    onSuccess: (view) => {
      recordBreadcrumb('register', 'turned a return into an exchange');
      onSaleChanged(view);
      focusSearch();
    },
  });

  const settleExchange = useMutation({
    mutationFn: () => api.register.settleExchange.mutate({ saleId: sale.sale.id }),
    onSuccess: async (view) => {
      recordBreadcrumb('register', `settled an exchange — credit ${view.credit.amountCents}`);
      void queryClient.invalidateQueries({ queryKey: ['register', 'exchange'] });

      /*
       * An even swap is *finished* by this press.
       *
       * Both halves reach zero, so the pair completes and there is nothing left
       * to collect. The register used to carry on as though the exchange were
       * still in progress, leaving the operator on a completed sale that then
       * refused a payment, refused another item, and refused to be cancelled —
       * with no receipt printed and no way out (§2.2-11). Pressing "Apply
       * credit" *is* the finalising press when the swap is even, and it says so.
       */
      if (view.completed && view.saleNumber !== null) {
        recordBreadcrumb('register', `completed ${view.saleNumber}`);
        onCompleted(view.saleNumber, 0, 'exchange');
        return;
      }

      onSaleChanged(await api.register.getSale.query({ saleId: sale.sale.id }));
    },
  });

  /**
   * Changed their mind — put it back to being a plain return (§2.2-11).
   *
   * "Exchange instead" used to be a one-way door: once pressed, the only way
   * onward was to find something to swap into. The customer who then says
   * "actually, just put it back on my card" is not an edge case, and the
   * cashier's only escape was cancelling the return and picking the lines
   * again.
   */
  const cancelExchange = useMutation({
    mutationFn: () => api.register.cancelExchange.mutate({ saleId: sale.sale.id }),
    onSuccess: (view) => {
      recordBreadcrumb('register', 'went back to a plain return');
      void queryClient.invalidateQueries({ queryKey: ['register', 'exchange'] });
      onSaleChanged(view);
    },
  });

  /*
   * This half is square, and what is left is on the other document (§8.5).
   *
   * After settling, exactly one half can still owe. The other one is at zero —
   * and offering "Pay cash · $0.00" there is not merely useless, it is a button
   * that takes a payment of nothing and looks like the way to finish. The real
   * action is going to the half the money is on, so that is what the primary
   * becomes.
   */
  const owedElsewhereCents =
    inExchange && creditApplied && outstandingCashCents === 0 && exchange.data != null
      ? exchange.data.dueFromCustomerCents > 0
        ? exchange.data.dueFromCustomerCents
        : exchange.data.dueToCustomerCents
      : 0;

  /** Move to the other half — where the money that is still owed actually is. */
  const switchHalf = useMutation({
    mutationFn: (saleId: string) => api.register.getSale.query({ saleId }),
    onSuccess: (view) => onSaleChanged(view),
  });

  return (
    <Screen
      title={isRefund ? 'Refund' : 'Register'}
      subtitle={isRefund ? 'Money going back to the customer.' : undefined}
      /*
       * The frame is pinned so the row below cannot scroll away (decision R3).
       * This is the half of the change that makes the other half safe — see
       * `.screen--fixed-frame`.
       */
      fixedFrame
      context={
        /*
         * The controls that *build* the sale, at the top with the context
         * (decision R3, §2.2-4).
         *
         * They were on the bottom bar beside *Pay cash* and *Pay card*, which
         * put five choices down there doing two different jobs: three that end
         * the sale and three that build it. §2.2-1 reserves the bottom for the
         * primary action and its destructive counterweight, and five dilutes
         * both — so splitting them makes the anti-misfire geometry *stronger*,
         * not weaker, because there is now more space between *Cancel* and
         * *Pay*.
         *
         * It also puts the control where its result is displayed (§2.2-3): the
         * customer button carries the attached customer's name, so it *is* the
         * display, and two independent groups of users went looking for it at
         * the top. It was already at the top on a refund and at the bottom on a
         * sale, which was the real inconsistency.
         *
         * Order is `Add customer · Hold · More · N on hold`. *More* sits after
         * *Hold* rather than before it because §2.2-2 defines it as the one
         * affordance rare options hide behind — so by construction it is the
         * least-used control in the row, and a container of rare things between
         * two frequent single actions is a speed bump on the common path.
         */
        <div className="register__context">
          <div className="register__context-actions">
            {/*
              On a refund the customer is inherited and cannot be changed, so
              this is pure context rather than a control (§2.2-13 — no button
              where there is no action).
            */}
            {isRefund ? (
              <CustomerOnSale
                saleId={sale.sale.id}
                customer={sale.customer}
                readOnly
                onAttached={onSaleChanged}
              />
            ) : (
              <CustomerOnSale
                saleId={sale.sale.id}
                customer={sale.customer}
                onAttached={onSaleChanged}
              />
            )}
            {isRefund ? null : (
              <Button
                variant="ghost"
                disabledReason={isEmpty ? 'Nothing to hold' : undefined}
                onClick={() => setDialog('hold')}
              >
                {/* A part-paid sale can be held too: step the customer aside
                  rather than block the queue (§2.2-9). */}
                {partPaid ? 'Hold for a manager' : 'Hold'}
              </Button>
            )}
            {/*
            Progressive disclosure, exactly as §2.2-2 prescribes: selling a gift
            card and starting a layaway are real but occasional, and putting
            each on its own control would take a counter past the point where a
            decision stays fast. One affordance, several things behind it.
          */}
            {isRefund || inExchange ? null : (
              <Button variant="ghost" onClick={() => setDialog('more')}>
                More
              </Button>
            )}
            {heldCount > 0 && !isRefund ? (
              /* The "what's outstanding" cue (§2.2-9). Held work is invisible
                 otherwise, and invisible work gets forgotten. Last in the row,
                 where it already was — it is a status that happens to be
                 pressable, not an action. */
              <Button variant="ghost" onClick={() => setDialog('holds')}>
                {heldCount} on hold
              </Button>
            ) : null}
          </div>

          {/*
            Beneath the controls, never among them (revision item 4a).
            Appearing here grows the header downwards when a customer is
            attached; putting it in the row above would move *Hold* and *More*
            sideways instead, which is the one thing decision R3 set out to
            prevent.
          */}
          <CustomerDetail customer={sale.customer} />
        </div>
      }
      destructiveAction={
        /*
         * Always available. There must never be a state a cashier cannot get
         * out of — an empty refund cart with a disabled Cancel button left the
         * register with no way back to a normal sale, which is the worst kind
         * of bug because the counter simply stops. The only thing that blocks
         * it is money outstanding, and that has its own way out (refund it).
         */
        <Button
          variant="danger"
          disabledReason={
            partPaid
              ? 'Refund the payment taken so far before canceling'
              : creditApplied
                ? // Cancelling one half of a netted pair would leave the other
                  // holding a credit against a document that no longer exists.
                  // The server refuses it; saying so here means the operator
                  // never presses a button that cannot work (§2.2-7).
                  'Use “Just refund instead” — an exchange is unwound, not half-canceled'
                : undefined
          }
          onClick={() => (isEmpty ? cancelSale.mutate() : setDialog('cancel'))}
          busy={cancelSale.isPending}
        >
          {isRefund ? 'Cancel refund' : isEmpty ? 'Start over' : 'Cancel sale'}
        </Button>
      }
      secondaryActions={
        /*
         * While a payment is open, the way back is the only other thing here
         * (FB-1121, FB-1123). Pay cash and Pay card stayed lit beside the panel
         * and did nothing — the panel had already chosen — and with store credit
         * chosen they offered two other ways to pay at once. How to pay is
         * chosen in one place: the panel's own row of methods.
         */
        tendering !== null ? (
          <Button variant="secondary" size="large" onClick={backToCart}>
            Back to cart
          </Button>
        ) : (
          <>
            {/*
            The §8.5 entry point. It sits *here*, on a refund whose lines are
            already picked, because that is the moment the credit is known — and
            it keeps a plain return costing nothing extra, since refunding stays
            the default action bottom-right (§2.2-5).
          */}
            {isRefund && !inExchange ? (
              <Button
                variant="ghost"
                busy={startExchange.isPending}
                disabledReason={
                  isEmpty
                    ? 'Choose what is coming back first'
                    : partPaid
                      ? 'This refund has already been paid out'
                      : undefined
                }
                onClick={() => startExchange.mutate()}
              >
                Exchange instead
              </Button>
            ) : null}
            {/*
            *Add customer*, *Hold* and *More* used to live here. They build the
            sale rather than ending it, so decision R3 moved them into the
            header with the rest of the context — leaving this bar with one
            primary, one alternative and one destructive at the far end, which
            is the geometry §2.2-1 asks for.
          */}
            {/*
            The card figure belongs on the button, not two taps inside the
            tender screen. "What will it be on the card?" is asked at the
            counter before the customer decides how to pay, and an operator who
            has to open a panel to answer it reads the cash total out instead.
            `packages/core/tender.ts` has specified this since the money engine
            was written; only the cash button had it.
          */}
            {/*
            Not while the exchange is unsettled. Until the credit is applied
            nobody knows which way the difference runs — or whether there is
            one — so offering to charge a card at that step asks for money that
            may not be owed, and the amount beside it would be the full price of
            the replacement rather than anything the customer will pay. The one
            action here is applying the credit, and it is the primary (§2.2-12,
            one focal point).
          */}
            {(inExchange && !isRefund && !creditApplied) || owedElsewhereCents > 0 ? null : (
              <Button
                variant="secondary"
                size="large"
                disabledReason={isEmpty ? 'Add an item first' : undefined}
                onClick={() => goToPayment('card')}
              >
                {isRefund ? 'Refund to card' : 'Pay card'}
                {dualPricing || settledCents !== 0
                  ? ` · ${money(Math.abs(outstandingCardCents))}`
                  : ''}
              </Button>
            )}
          </>
        )
      }
      primaryAction={
        // The panel's own button takes the money while a payment is open.
        tendering !== null ? undefined /*
         * On the sale half of an unsettled exchange the primary action is
         * netting the two documents, not taking money: until the credit is
         * applied nobody knows which way the difference runs, and asking for
         * the full price of the replacement would be wrong at the counter.
         */ : owedElsewhereCents > 0 ? (
          /*
           * The difference is on the other document, so this is a *navigation*
           * dressed as the primary action rather than a payment. It carries the
           * amount so the operator reads the same figure here, on the banner
           * above, and on the payment screen they land on.
           */
          <Button
            variant="primary"
            size="large"
            busy={switchHalf.isPending}
            onClick={() =>
              switchHalf.mutate(
                isRefund ? exchange.data!.replacementSaleId : exchange.data!.refundSaleId,
              )
            }
          >
            {isRefund ? 'Take the difference' : 'Give back the difference'} ·{' '}
            {money(owedElsewhereCents)}
          </Button>
        ) : inExchange && !isRefund && !creditApplied ? (
          <Button
            variant="primary"
            size="large"
            busy={settleExchange.isPending}
            disabledReason={isEmpty ? 'Scan what the customer is taking' : undefined}
            onClick={() => settleExchange.mutate()}
          >
            {/*
              No amount until there is one. The credit is what applying it now
              would *cover*, so it is genuinely zero with an empty cart — and
              "Apply credit · $0.00" reads as though the return were worthless
              rather than as though nothing had been scanned yet.
            */}
            {(exchange.data?.credit.amountCents ?? 0) === 0
              ? 'Apply credit'
              : `Apply credit · ${money(exchange.data!.credit.amountCents)}`}
          </Button>
        ) : (
          <Button
            variant="primary"
            size="large"
            disabledReason={
              isEmpty
                ? isRefund
                  ? 'Choose what is coming back'
                  : 'Scan or search for an item'
                : undefined
            }
            onClick={() => goToPayment('cash')}
          >
            {/*
              What is *left*, not what the sale is worth. Showing the total
              after a credit or a part-payment put a different number on the
              button than the one the payment screen then asked for, and the
              operator has to read that figure out to the customer before the
              screen changes.
            */}
            {isRefund ? 'Refund cash' : 'Pay cash'} · {money(Math.abs(outstandingCashCents))}
          </Button>
        )
      }
    >
      {/*
        The backstop against a trapped register (§2.2-11).
        *
        An even-swap exchange completed both halves server-side while the screen
        carried on as though the sale were live — so every button then refused
        it in turn ("this sale is completed and can no longer take a payment",
        "…be changed"), Cancel was blocked because the credit was applied, and
        there was no way forward at all. The specific cause is fixed above; this
        catches every other route to the same place, because a counter that
        cannot get to the next customer stops the shop.
      */}
      {sale.sale.status !== 'open' && sale.sale.status !== 'held' ? (
        <Callout
          tone="warning"
          title={`This sale is ${sale.sale.status} — nothing more can be done to it here`}
        >
          It is finished, and its paperwork is on the Sales screen if you need to reprint or return
          against it.
          <div className="register__exchange-actions">
            <Button variant="primary" onClick={onSaleCleared}>
              Start a new sale
            </Button>
          </div>
        </Callout>
      ) : null}

      {inExchange ? (
        <ExchangeBanner
          view={exchange.data ?? null}
          isRefundHalf={isRefund}
          creditApplied={creditApplied}
          switching={switchHalf.isPending}
          onSwitch={(saleId) => switchHalf.mutate(saleId)}
          reverting={cancelExchange.isPending}
          onRevert={partPaid ? null : () => cancelExchange.mutate()}
        />
      ) : null}
      {layawayChange !== null ? (
        <Callout tone="warning" title={`Give ${money(layawayChange)} change`}>
          The deposit is on the layaway; this is what is left of what they handed over.
          <div className="register__exchange-actions">
            <Button variant="secondary" onClick={() => setLayawayChange(null)}>
              Done
            </Button>
          </div>
        </Callout>
      ) : null}
      {cancelExchange.isError ? (
        <Callout tone="danger">{errorMessage(cancelExchange.error)}</Callout>
      ) : null}
      {settleExchange.isError ? (
        <Callout tone="danger">{errorMessage(settleExchange.error)}</Callout>
      ) : null}
      {startExchange.isError ? (
        <Callout tone="danger">{errorMessage(startExchange.error)}</Callout>
      ) : null}

      {/*
        An exchange credit is not an outstanding payment — it is the other half
        of this transaction, and saying "already paid" about it would send the
        operator looking for money that never moved.
      */}
      {partPaid && !creditApplied ? (
        <Callout tone="warning" title={`${money(paidCents)} already paid`}>
          The cart is locked while a payment is outstanding. Take the rest, refund what was paid, or
          hold the sale for a manager — no merchandise has changed hands.
        </Callout>
      ) : null}

      <div className="register">
        <div className="register__cart">
          {/* Nothing is scanned onto a refund; the lines come from the receipt. */}
          {isRefund ? null : (
            <SearchBar
              inputRef={searchRef}
              onPick={(variantId) => addItem.mutate(variantId)}
              onPickConsignment={(itemId) => addConsignment.mutate(itemId)}
              onSellGiftCard={() => setDialog('giftCard')}
              giftCardBlockedReason={
                sale.customer === null
                  ? 'A gift card is money — attach a customer first'
                  : undefined
              }
              busy={addItem.isPending || addConsignment.isPending}
            />
          )}

          {isEmpty ? (
            <div className="cart__empty">
              <p className="text-secondary">
                {isRefund ? 'Nothing selected to return.' : 'Scan an item to start the sale.'}
              </p>
            </div>
          ) : (
            <Card padded={false}>
              {/*
                Why a line cannot be opened, said once for the whole cart and
                pointed at by every locked line (DESIGN-DNA: disabled says why) —
                the browser suite's every-journey check found these silent.
              */}
              {isRefund || partPaid ? (
                <span id={lockedReasonId} className="visually-hidden">
                  {isRefund
                    ? 'On a refund, what comes back was chosen on the receipt.'
                    : 'Payment has started. Refund what was paid before changing the cart.'}
                </span>
              ) : null}
              <ul className="cart">
                {sale.lines.map((line) => (
                  <li key={line.id} className="cart-line">
                    {/*
                      The line itself is the target (§2.2-1, Fitts) — the whole
                      name and meta block, not a small icon beside it. Tapping
                      it is the first of the three interactions §2.2 allows for
                      a price override. On a refund it does nothing: what is
                      coming back was decided on the receipt.
                    */}
                    <button
                      type="button"
                      className="cart-line__main cart-line__main--tappable"
                      disabled={isRefund || partPaid}
                      aria-describedby={isRefund || partPaid ? lockedReasonId : undefined}
                      aria-label={`Adjust ${line.description}`}
                      onClick={() => setAdjustingId(line.id)}
                    >
                      <span className="cart-line__name">{line.description}</span>
                      <span className="cart-line__meta">
                        <Mono>{line.sku ?? '—'}</Mono>
                        {line.priceOverridden ? <Badge tone="warning">Override</Badge> : null}
                        {line.lineDiscountCents > 0 ? (
                          <Badge tone="success">−{money(line.lineDiscountCents)}</Badge>
                        ) : null}
                        {line.note != null && line.note !== '' ? (
                          <span className="cart-line__note">{line.note}</span>
                        ) : null}
                        {/* It still sells (decision 172) — but it says the shelf is
                            short, so the count is put right before it matters. */}
                        {(() => {
                          const short = sale.short?.find((entry) => entry.lineId === line.id);
                          return short === undefined ? null : (
                            <Badge tone="warning">Only {short.available} on hand</Badge>
                          );
                        })()}
                      </span>
                    </button>

                    {isRefund ? (
                      /* What is coming back was chosen on the receipt; the
                         register does not re-open that decision. Nudging a
                         negative quantity here deleted the line. */
                      <span className="num qty-static">{Math.abs(line.quantity)}</span>
                    ) : (
                      <QuantityControl
                        quantity={line.quantity}
                        label={line.description}
                        disabled={partPaid}
                        reasonId={partPaid ? lockedReasonId : undefined}
                        oneOfAKind={line.consignmentItemId != null}
                        onChange={(quantity) => setQuantity.mutate({ lineId: line.id, quantity })}
                      />
                    )}

                    <div className="cart-line__money">
                      <div className="cart-line__price money">{money(line.netCents)}</div>
                      <div className="cart-line__unit money">
                        {line.quantity} × {money(line.unitPriceCents)}
                      </div>
                      {/*
                        The line's other posted price. Shown per line rather
                        than only on the total because the question at the
                        counter is usually about one item — "how much is the
                        helmet on a card?" — and a sale total cannot answer it.
                      */}
                      {dualPricing && line.cardNetCents != null ? (
                        <div className="cart-line__card money">
                          {money(line.cardNetCents)} on card
                        </div>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ul>
            </Card>
          )}
        </div>

        <div className="register__totals">
          {tendering === null ? (
            <Totals
              sale={sale}
              // A sale-level discount is meaningless on an empty cart and wrong
              // on a refund, so the control is absent rather than refusing
              // (§2.2-13).
              onDiscount={isEmpty || isRefund || partPaid ? null : () => setDialog('saleDiscount')}
              // The exchange credit, whether it has been applied yet or not, and
              // what is genuinely left to move. Without these the panel reports
              // the full price of the replacement while the banner above it
              // reports the difference, and the two disagree on screen.
              creditCents={inExchange ? (exchange.data?.credit.amountCents ?? null) : null}
              creditApplied={creditApplied}
              outstandingCents={outstandingCashCents}
            />
          ) : (
            <TenderPanel
              sale={sale}
              registerId={registerId}
              cards={cards}
              cardPress={cardPress}
              onCardPressUsed={() => setCardPress(false)}
              method={tendering}
              onMethod={setTendering}
              onRefundRequested={() => setDialog('refund')}
              onSaleChanged={onSaleChanged}
              onCompleted={onCompleted}
            />
          )}
        </div>
      </div>

      {addItem.isError ? <Callout tone="danger">{errorMessage(addItem.error)}</Callout> : null}
      {addConsignment.isError ? (
        <Callout tone="danger">{errorMessage(addConsignment.error)}</Callout>
      ) : null}
      {setQuantity.isError ? (
        <Callout tone="danger">{errorMessage(setQuantity.error)}</Callout>
      ) : null}

      {/*
        Two large targets and nothing else, the same shape the feedback button
        uses (§19.1). A menu of six would defeat the point of moving them here.
      */}
      <Modal
        open={dialog === 'more'}
        title="What else?"
        onClose={() => setDialog(null)}
        secondaryAction={
          <Button variant="ghost" onClick={() => setDialog(null)}>
            Back to the sale
          </Button>
        }
      >
        <div className="register__more">
          <Button
            variant="secondary"
            size="large"
            // A gift card is bearer value. If it is lost or disputed the only
            // question anyone can ask is who bought it, and an anonymous sale
            // has no answer — so the customer is asked for up front rather than
            // refused at the end (§2.2-7).
            disabledReason={
              sale.customer === null ? 'A gift card is money — attach a customer first' : undefined
            }
            onClick={() => setDialog('giftCard')}
          >
            Sell a gift card
          </Button>
          <Button
            variant="secondary"
            size="large"
            disabledReason={
              isEmpty
                ? 'Scan what they are putting away first'
                : sale.customer === null
                  ? 'A layaway is held for a person — attach a customer first'
                  : partPaid
                    ? 'Payment has already started on this sale'
                    : undefined
            }
            onClick={() => setDialog('layaway')}
          >
            Put on layaway
          </Button>

          {/*
            The fix goes where the refusal is (§2.2-11).

            Both choices above need a customer and both said so, which is right
            — but the control that satisfies them was on the action bar behind a
            "Back to the sale" press, so the operator had to dismiss the thing
            they were trying to do, attach someone, and find their way back. A
            reason without a remedy beside it is only half of error prevention.
          */}
          {/*
            The drawer's own actions, behind one affordance (§2.2-2).
            *
            Four choices — drop, pay out, pay in, no sale — that are one job:
            money moving without a sale. On the bar they would be four more
            things competing with Pay cash; here they are one press away and
            grouped the way an operator thinks about them.
          */}
          <Button
            variant="secondary"
            size="large"
            disabledReason={
              sale.sale.drawerSessionId === null ? 'No drawer is open on this register' : undefined
            }
            onClick={() => setDialog('drawer')}
          >
            Drawer — drop, pay in or out
          </Button>
          {/*
            The end of the shift (2026-09-24). The server has always been able
            to close a drawer; nothing on screen asked it to, so End of day
            said "close it to get a counted figure" about a drawer nobody could
            close.
          */}
          <Button
            variant="secondary"
            size="large"
            disabledReason={
              sale.sale.drawerSessionId === null ? 'No drawer is open on this register' : undefined
            }
            onClick={() => setDialog('closeDrawer')}
          >
            Close the drawer
          </Button>

          {sale.customer === null ? (
            <div className="register__more-fix">
              <span className="text-secondary">Both of these need a customer on the sale.</span>
              <CustomerOnSale
                saleId={sale.sale.id}
                customer={sale.customer}
                onAttached={onSaleChanged}
              />
            </div>
          ) : null}
        </div>
      </Modal>

      {dialog === 'drawer' && sale.sale.drawerSessionId !== null ? (
        <DrawerMovementDialog
          drawerSessionId={sale.sale.drawerSessionId}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === 'closeDrawer' && sale.sale.drawerSessionId !== null ? (
        <CloseDrawerDialog
          drawerSessionId={sale.sale.drawerSessionId}
          onClose={() => setDialog(null)}
        />
      ) : null}

      {dialog === 'giftCard' ? (
        <GiftCardDialog
          saleId={sale.sale.id}
          onClose={() => setDialog(null)}
          onAdded={(view) => {
            setDialog(null);
            onSaleChanged(view);
          }}
        />
      ) : null}

      {dialog === 'layaway' ? (
        <LayawayDialog
          registerId={registerId}
          cards={cards}
          sale={sale}
          onClose={() => setDialog(null)}
          onCreated={(result) => {
            setDialog(null);
            if (result.changeCents > 0) setLayawayChange(result.changeCents);

            if (!result.split) {
              // The whole cart is now a layaway and no longer this register's,
              // so the screen goes back to a fresh sale rather than showing a
              // closed one.
              onSaleCleared();
              return;
            }

            // Only part of it went away. The customer is still standing there
            // waiting to pay for the rest, so the cart carries on — with the
            // laid-away lines gone and the totals redone.
            void api.register.getSale.query({ saleId: sale.sale.id }).then((view) => {
              onSaleChanged(view);
              focusSearch();
            });
          }}
        />
      ) : null}

      <HoldDialog
        open={dialog === 'hold'}
        busy={hold.isPending}
        error={hold.isError ? errorMessage(hold.error) : null}
        onClose={() => setDialog(null)}
        onHold={(name) => hold.mutate(name)}
      />

      <HoldsDialog
        open={dialog === 'holds'}
        holds={holds.data ?? []}
        busy={resume.isPending}
        error={resume.isError ? errorMessage(resume.error) : null}
        cartHasItems={!isEmpty}
        onClose={() => setDialog(null)}
        onResume={(saleId) => resume.mutate(saleId)}
      />

      <Modal
        open={dialog === 'cancel'}
        tone="danger"
        title="Cancel this sale?"
        description={`${sale.lines.length} item${sale.lines.length === 1 ? '' : 's'} will be cleared and a new sale started. No payment has been taken.`}
        onClose={() => setDialog(null)}
        secondaryAction={
          <>
            <Button variant="ghost" onClick={() => setDialog(null)}>
              Back to cart
            </Button>
            {/* Holding is almost always what was meant on a sale, so it happens
                here in one press rather than dismiss-then-hold. A refund has
                nothing to park: it is a decision already made, being executed. */}
            {isRefund ? null : (
              <Button variant="secondary" onClick={() => setDialog('hold')}>
                Hold instead
              </Button>
            )}
          </>
        }
        primaryAction={
          <Button variant="danger" busy={cancelSale.isPending} onClick={() => cancelSale.mutate()}>
            {isRefund ? 'Cancel refund' : 'Cancel sale'}
          </Button>
        }
      >
        {cancelSale.isError ? (
          <Callout tone="danger">{errorMessage(cancelSale.error)}</Callout>
        ) : null}
      </Modal>

      <RefundPaymentDialog
        open={dialog === 'refund'}
        payments={livePayments}
        busy={refundTender.isPending}
        error={refundTender.isError ? errorMessage(refundTender.error) : null}
        needsApproval={refundTender.isError && isOverridable(refundTender.error)}
        approvalError={refundTender.isError ? approvalRefusal(refundTender.error) : null}
        onClose={() => setDialog(null)}
        onRefund={(tenderId, reason, override) =>
          refundTender.mutate({ tenderId, reason, ...(override === undefined ? {} : { override }) })
        }
      />

      {/* Line and sale adjustments (SCOPE §8.1). */}
      {adjustingLine !== undefined ? (
        <LineAdjustDialog
          saleId={sale.sale.id}
          line={adjustingLine}
          thresholdPpm={thresholdPpm}
          onClose={() => setAdjustingId(null)}
          onSaleChanged={onSaleChanged}
        />
      ) : null}

      {dialog === 'saleDiscount' ? (
        <SaleDiscountDialog
          saleId={sale.sale.id}
          grossSubtotalCents={sale.sale.grossSubtotalCents}
          hasDiscount={sale.sale.discountKind !== null}
          thresholdPpm={thresholdPpm}
          onClose={() => setDialog(null)}
          onSaleChanged={onSaleChanged}
        />
      ) : null}

      {/*
        The ask, on the way to payment. Skipping continues to the same tender
        screen the button was already heading for, so a "no thanks" costs one
        tap and never a restart (§2.2-11).
      */}
      {promptingCustomer !== null ? (
        <CustomerPrompt
          saleId={sale.sale.id}
          onResolved={(view) => {
            const next = promptingCustomer;
            setAskedForCustomer(sale.sale.id);
            setPromptingCustomer(null);
            if (view !== null) onSaleChanged(view);
            if (next !== null) setTendering(next);
          }}
        />
      ) : null}
    </Screen>
  );
}

// ---------------------------------------------------------------------------

/**
 * Quantity: nudge it, or say it.
 *
 * The buttons are right for one or two; clicking the number and typing is right
 * for twelve. Making someone press `+` eleven times is the kind of small
 * cruelty that adds up over a season (§2-3, fewest steps).
 */
function QuantityControl({
  quantity,
  label,
  disabled,
  reasonId,
  onChange,
  oneOfAKind = false,
}: {
  quantity: number;
  label: string;
  disabled: boolean;
  /** Why it is disabled: the id of the words that say so (DESIGN-DNA). */
  reasonId?: string | undefined;
  onChange: (quantity: number) => void;
  /**
   * A consigned item (decision 131): there is only one, so `+` and typing a
   * number could only be refused and are not shown (§2.2-13). `−` stays — it
   * is how a line comes out of the cart.
   */
  oneOfAKind?: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(String(quantity));

  const commit = () => {
    const parsed = Number.parseInt(draft, 10);
    setEditing(false);
    if (Number.isFinite(parsed) && parsed >= 0 && parsed !== quantity) onChange(parsed);
  };

  return (
    <div className="cart-line__qty">
      <button
        type="button"
        className="qty-button"
        aria-label={`One fewer ${label}`}
        disabled={disabled}
        aria-describedby={disabled ? reasonId : undefined}
        onClick={() => onChange(quantity - 1)}
      >
        −
      </button>

      {oneOfAKind ? (
        <span className="num qty-static">{quantity}</span>
      ) : editing ? (
        <input
          className="qty-input num"
          inputMode="numeric"
          autoFocus
          aria-label={`Quantity of ${label}`}
          value={draft}
          onChange={(event) => setDraft(event.target.value.replace(/\D/g, '').slice(0, 3))}
          onBlur={commit}
          onKeyDown={(event) => {
            if (event.key === 'Enter') {
              event.preventDefault();
              commit();
            } else if (event.key === 'Escape') {
              event.preventDefault();
              setDraft(String(quantity));
              setEditing(false);
            }
          }}
        />
      ) : (
        <button
          type="button"
          className="qty-value num"
          aria-label={`Quantity of ${label} is ${quantity}. Click to type a different number.`}
          disabled={disabled}
          aria-describedby={disabled ? reasonId : undefined}
          onClick={() => {
            setDraft(String(quantity));
            setEditing(true);
          }}
        >
          {quantity}
        </button>
      )}

      {oneOfAKind ? null : (
        <button
          type="button"
          className="qty-button"
          aria-label={`One more ${label}`}
          disabled={disabled}
          aria-describedby={disabled ? reasonId : undefined}
          onClick={() => onChange(quantity + 1)}
        >
          +
        </button>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

// Kept in its own module so the card panel can use it too (no import cycle).
export { sanitiseAmount } from './amount.js';

/**
 * Give back a payment that has already been taken.
 *
 * Always manager-gated — it moves money that is physically in the drawer — so
 * the PIN prompt is not an edge case here, it is the normal path for an
 * associate. Before this was wired, an associate could reach this dialog, press
 * Refund, and get a bare "Permission denied" with a customer waiting and a
 * cart that could not be unlocked any other way (§2-6, §2.2-11).
 */
function RefundPaymentDialog({
  open,
  payments,
  busy,
  error,
  needsApproval,
  approvalError,
  onClose,
  onRefund,
}: {
  open: boolean;
  payments: { id: string; kind: string; amountCents: number; changeGivenCents: number }[];
  busy: boolean;
  error: string | null;
  needsApproval: boolean;
  approvalError: string | null;
  onClose: () => void;
  onRefund: (tenderId: string, reason: string, override?: ManagerOverride) => void;
}) {
  const [reason, setReason] = useState('');
  const [pendingTenderId, setPendingTenderId] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setPendingTenderId(null);
    }
  }, [open]);

  if (open && needsApproval && pendingTenderId !== null) {
    const payment = payments.find((entry) => entry.id === pendingTenderId);
    return (
      <ManagerApproval
        title="Manager approval needed"
        because={`Giving back ${money(
          (payment?.amountCents ?? 0) - (payment?.changeGivenCents ?? 0),
        )} that has already been taken.`}
        busy={busy}
        error={approvalError}
        onCancel={onClose}
        onApprove={(override) => onRefund(pendingTenderId, reason.trim(), override)}
      />
    );
  }

  return (
    <Modal
      open={open}
      title="Refund a payment"
      description="Give the money back and unlock the cart. A manager PIN may be asked for."
      onClose={onClose}
      primaryAction={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      <label className="field__label" htmlFor="refund-reason">
        Why?
      </label>
      <input
        id="refund-reason"
        className="input"
        autoFocus
        maxLength={200}
        placeholder="Second card declined"
        value={reason}
        onChange={(event) => setReason(event.target.value)}
      />

      <ul className="holds">
        {payments.map((payment) => (
          <li key={payment.id}>
            <button
              type="button"
              className="holds__item"
              disabled={busy || reason.trim() === ''}
              aria-describedby={`refund-why-${payment.id}`}
              aria-busy={busy ? true : undefined}
              onClick={() => {
                // Remembered so the approval prompt knows which payment it is
                // approving, and can retry it once the PIN is in.
                setPendingTenderId(payment.id);
                onRefund(payment.id, reason.trim());
              }}
            >
              <span className="holds__name">
                Refund {payment.kind === 'gift_card' ? 'gift card' : payment.kind}
              </span>
              <span id={`refund-why-${payment.id}`} className="text-secondary">
                {reason.trim() === '' ? 'Give a reason first' : 'Tap to refund'}
              </span>
              <span className="holds__total money">
                {money(payment.amountCents - payment.changeGivenCents)}
              </span>
            </button>
          </li>
        ))}
      </ul>

      {error !== null ? <Callout tone="danger">{error}</Callout> : null}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function HoldDialog({
  open,
  busy,
  error,
  onClose,
  onHold,
}: {
  open: boolean;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onHold: (name: string) => void;
}) {
  const [name, setName] = useState('');

  useEffect(() => {
    if (open) setName('');
  }, [open]);

  const trimmed = name.trim();

  return (
    <Modal
      open={open}
      title="Hold this sale"
      description="Give it a name you will recognize when the customer comes back."
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={busy}
          disabledReason={trimmed === '' ? 'Give the hold a name' : undefined}
          onClick={() => onHold(trimmed)}
        >
          Hold sale
        </Button>
      }
    >
      <input
        className="input"
        autoFocus
        maxLength={60}
        placeholder="red jacket lady"
        value={name}
        onChange={(event) => setName(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && trimmed !== '') {
            event.preventDefault();
            onHold(trimmed);
          }
        }}
      />
      {error !== null ? <Callout tone="danger">{error}</Callout> : null}
    </Modal>
  );
}

function HoldsDialog({
  open,
  holds,
  busy,
  error,
  cartHasItems,
  onClose,
  onResume,
}: {
  open: boolean;
  holds: {
    id: string;
    holdName: string | null;
    totalCents: number;
    employeeName: string | null;
  }[];
  busy: boolean;
  error: string | null;
  cartHasItems: boolean;
  onClose: () => void;
  onResume: (saleId: string) => void;
}) {
  return (
    <Modal
      open={open}
      title="Sales on hold"
      description="Pick one up where it was left."
      onClose={onClose}
      primaryAction={
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      }
    >
      {cartHasItems ? (
        <Callout tone="warning">
          The current cart has items in it. Hold or finish it before resuming another.
        </Callout>
      ) : null}

      {holds.length === 0 ? (
        <p className="text-secondary">Nothing is on hold.</p>
      ) : (
        <ul className="holds">
          {holds.map((entry) => (
            <li key={entry.id}>
              <button
                type="button"
                className="holds__item"
                disabled={busy || cartHasItems}
                onClick={() => onResume(entry.id)}
              >
                <span className="holds__name">{entry.holdName ?? 'Unnamed hold'}</span>
                <span className="text-secondary">{entry.employeeName ?? 'Unknown'}</span>
                <span className="holds__total money">{money(entry.totalCents)}</span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {error !== null ? <Callout tone="danger">{error}</Callout> : null}
    </Modal>
  );
}

// ---------------------------------------------------------------------------

function SearchBar({
  inputRef,
  onPick,
  onPickConsignment,
  onSellGiftCard,
  giftCardBlockedReason,
  busy,
}: {
  inputRef: React.RefObject<HTMLInputElement | null>;
  onPick: (variantId: string) => void;
  /** A consigned item, found by the number on its label (decision 131). */
  onPickConsignment: (itemId: string) => void;
  /** A gift card is not a catalog row; searching for one opens its dialog. */
  onSellGiftCard: () => void;
  /** Why selling one is not possible yet, if it is not (mirrors the More button). */
  giftCardBlockedReason: string | undefined;
  busy: boolean;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');
  /*
   * A style as a grid, and the department walk (decision 182): "a grid view
   * of the variant in the search dropdown … and department drill-down, like
   * you would on an Android touch screen".
   */
  const [gridFor, setGridFor] = useState<string | null>(null);
  const [browsing, setBrowsing] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term.trim()), 120);
    return () => clearTimeout(timer);
  }, [term]);

  useEffect(() => {
    inputRef.current?.focus();
  }, [inputRef]);

  /*
   * A scan works from wherever the operator was standing.
   *
   * Focus leaves this box constantly — a dialog closes, a cart line is tapped,
   * a button is pressed — and a scanner is a keyboard, so the next scan lands
   * nowhere and nothing happens on screen. The operator scans again, and again,
   * and concludes the hardware is broken. Restoring focus on every path that
   * loses it is a list nobody finishes; this is the backstop that does not
   * depend on remembering.
   */
  const appendTyped = useCallback((character: string) => {
    setTerm((current) => current + character);
  }, []);

  useTypingFocus(inputRef, appendTyped);

  /**
   * Does what they typed mean "I want to sell a gift card"?
   *
   * Matched on the term itself rather than waiting for the server, so the row
   * appears as the words are typed like every other result. Deliberately loose
   * — `gift`, `giftcard`, `gift card`, `gc` — because the cost of offering it
   * when they meant something else is one extra row in a list, and the cost of
   * not offering it is the operator concluding gift cards are not in here.
   */
  const offersGiftCard = /^(gift|gift\s*card|giftcard|gc)$/i.test(term.trim());

  const search = useQuery({
    queryKey: ['catalog', 'search', debounced],
    // As many as the API gives (50), in a list that scrolls — eight, with no
    // sign there were more, left a 60-size style unreachable (FB-1071, FB-1077).
    queryFn: () => api.catalog.search.query({ term: debounced, limit: SEARCH_LIMIT }),
    enabled: debounced.length > 0,
    staleTime: 10_000,
  });

  const results = search.data?.results ?? [];
  const consignment = search.data?.consignment ?? null;
  /** Why a consigned item found by its label cannot be sold, if it cannot. */
  const consignmentBlocked =
    consignment === null || consignment.status === 'for_sale'
      ? undefined
      : consignment.status === 'sold'
        ? 'Already sold'
        : 'Went back to its owner';

  /*
   * A scan, or a consignment label, that goes straight onto the sale. Its
   * results are never drawn: the list used to render for the one frame before
   * the effect below added the item and cleared the box — a list flashing up
   * on every beep, and over the new line on a slow machine (CI, 2026-10-16).
   */
  const autoPicked =
    (search.data?.scanned === true && search.data.results[0] !== undefined) ||
    search.data?.consignment?.status === 'for_sale';

  const clear = () => {
    setTerm('');
    setDebounced('');
  };

  // A scan resolves to exactly one item, so it goes straight into the cart —
  // making the operator pick from a list of one is a wasted interaction.
  useEffect(() => {
    if (search.data?.scanned === true && search.data.results[0] !== undefined) {
      onPick(search.data.results[0].variantId);
      clear();
    } else if (search.data?.consignment?.status === 'for_sale') {
      // A consignment label is a scan too: its number names exactly one item.
      onPickConsignment(search.data.consignment.id);
      clear();
    }
  }, [search.data, onPick, onPickConsignment]);

  const pickFromGrid = (variantId: string) => {
    onPick(variantId);
    setGridFor(null);
    setBrowsing(false);
    clear();
  };

  return (
    <div className="search">
      {gridFor !== null ? (
        <StyleGridPicker
          productId={gridFor}
          onPick={pickFromGrid}
          onClose={() => setGridFor(null)}
        />
      ) : null}
      {browsing ? <BrowseDialog onPick={pickFromGrid} onClose={() => setBrowsing(false)} /> : null}
      <div className="search__row">
        <input
          ref={inputRef}
          className="search__input"
          placeholder="Scan barcode or search model #, description, SKU…"
          value={term}
          autoComplete="off"
          spellCheck={false}
          onChange={(event) => setTerm(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' && results[0] !== undefined) {
              event.preventDefault();
              onPick(results[0].variantId);
              clear();
            } else if (event.key === 'Escape') {
              clear();
            }
          }}
        />
        <Button variant="secondary" className="search__browse" onClick={() => setBrowsing(true)}>
          Browse
        </Button>
      </div>

      {(results.length > 0 || offersGiftCard || consignment !== null) &&
      term !== '' &&
      !autoPicked ? (
        <ul className="search__results">
          {/*
            A gift card is not in the catalog, and looking for one here is the
            first thing anybody tries.

            It could not be: a gift card is bearer value with a ledger, a number
            and a liability figure, not a product — putting a real row in the
            catalog to make it findable would make it *sellable as a line*,
            which is how a card gets sold without a number and without an owner
            (decision 54). So the row is synthetic: it is only ever an entry
            point to the same dialog More opens, and it cannot be added to a
            cart by accident.

            First in the list, because somebody typing "gift card" has said
            plainly what they want and no product is a better answer.
          */}
          {offersGiftCard ? (
            <li key="sell-gift-card">
              <button
                type="button"
                className="search__result"
                disabled={giftCardBlockedReason !== undefined}
                onClick={() => {
                  onSellGiftCard();
                  clear();
                }}
              >
                <span className="search__result-name">Sell a gift card</span>
                <span className="search__result-meta">
                  {/* Disabled always says why (§2.2-7): a grey row with no
                      reason is what sends somebody looking for a manager. */}
                  <span className="text-secondary">
                    {giftCardBlockedReason ?? 'Scan the card and set the amount'}
                  </span>
                </span>
              </button>
            </li>
          ) : null}
          {/*
            A consigned item found by its label's number. Usually added before
            anyone sees this row; it stays when it cannot be sold, to say why.
          */}
          {consignment !== null ? (
            <li key={`consignment-${consignment.id}`}>
              <button
                type="button"
                className="search__result"
                disabled={busy || consignmentBlocked !== undefined}
                onClick={() => {
                  onPickConsignment(consignment.id);
                  clear();
                }}
              >
                <span className="search__result-name">{consignment.description}</span>
                <span className="search__result-meta">
                  <Mono>{consignment.number}</Mono>
                  <Badge tone={consignmentBlocked === undefined ? 'info' : 'neutral'}>
                    Consignment
                  </Badge>
                  {consignmentBlocked !== undefined ? (
                    <span className="text-secondary">{consignmentBlocked}</span>
                  ) : null}
                </span>
                <span className="search__result-price money">{money(consignment.priceCents)}</span>
              </button>
            </li>
          ) : null}
          {results.map((result, index) => (
            <Fragment key={result.variantId}>
              {/* Each style heads its sizes, with its grid one press away. */}
              {index === 0 || results[index - 1]!.productId !== result.productId ? (
                <li className="search__style">
                  <span>
                    {result.name} <Mono>{result.styleNumber}</Mono>
                  </span>
                  <Button
                    variant="ghost"
                    aria-label={`Sizes of ${result.styleNumber} in a grid`}
                    onClick={() => setGridFor(result.productId)}
                  >
                    Grid
                  </Button>
                </li>
              ) : null}
              <li>
                <button
                  type="button"
                  className="search__result"
                  onClick={() => {
                    onPick(result.variantId);
                    clear();
                  }}
                  disabled={busy}
                >
                  <span className="search__result-name">
                    {result.name}
                    {result.color != null ? ` · ${result.color}` : ''}
                    {result.size != null ? ` · ${result.size}` : ''}
                    {/* The second axis, where there is one — two trousers that
                      differ only by inseam have to be told apart *here*, before
                      one of them goes in the cart. */}
                    {result.altSize != null ? ` · ${result.altSize}` : ''}
                  </span>
                  <span className="search__result-meta">
                    <Mono>{result.sku}</Mono>
                    {result.available != null ? (
                      <span className={result.available > 0 ? 'text-secondary' : 'stock-out'}>
                        {/* A hold is units on the shelf that are not for sale —
                          a web cart, an online order, a layaway. Shown as
                          "available" beside "held", not as fewer "on hand",
                          which read as sold (FB-1082). */}
                        {result.held != null && result.held > 0
                          ? `${result.available} available · ${result.held} held`
                          : `${result.available} on hand`}
                      </span>
                    ) : (
                      /*
                       * A service is priced and reported, never counted (§11.2),
                       * so `catalog.search` sends `available: null` — and this
                       * row rendered *nothing* where every other row says "N on
                       * hand". Silence in a column that is otherwise always full
                       * reads as missing data, not as "this one has no stock
                       * figure by design". Say which it is (§2.2-10).
                       */
                      <Badge tone="info">Service</Badge>
                    )}
                  </span>
                  <span className="search__result-price money">{money(result.cashPriceCents)}</span>
                  {index === 0 ? <kbd className="search__enter">Enter</kbd> : null}
                </button>
              </li>
            </Fragment>
          ))}
          {/* A bounded list says when it is bounded. */}
          {results.length >= SEARCH_LIMIT ? (
            <li key="more" className="search__more text-secondary">
              Showing the first {SEARCH_LIMIT} — type more to narrow it down.
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------

function Totals({
  sale,
  onDiscount,
  creditCents,
  creditApplied,
  outstandingCents,
}: {
  sale: SaleView;
  onDiscount: (() => void) | null;
  /** The exchange credit on this sale, if it is half of an exchange (§8.5). */
  creditCents?: number | null;
  creditApplied?: boolean;
  /** What is still to move once everything applied has been taken off. */
  outstandingCents?: number;
}) {
  const dualPricing =
    sale.sale.cardTotalCents != null && sale.sale.cardTotalCents !== sale.sale.totalCents;

  const credit = creditCents ?? null;
  const showsCredit = credit !== null && credit !== 0;
  const stillDue = outstandingCents ?? sale.sale.totalCents;

  return (
    <Card>
      <div className="totals-panel">
        <Row label="Subtotal" value={money(sale.sale.grossSubtotalCents)} />
        {sale.sale.discountCents > 0 ? (
          <Row label="Discounts" value={`−${money(sale.sale.discountCents)}`} tone="success" />
        ) : null}
        {/*
          Discounting the whole sale lives beside the subtotal it acts on, not
          in the action bar — the action bar belongs to tender (§2.2-4). It says
          what it will do rather than "Discount", so nobody has to guess whether
          it means the line or the basket.
        */}
        {onDiscount !== null ? (
          <button type="button" className="totals-panel__discount" onClick={onDiscount}>
            {sale.sale.discountKind === null ? 'Discount the sale' : 'Change the sale discount'}
          </button>
        ) : null}
        {/*
          Cash-price tax; the card-price adjustment is not itemised (§8.2).

          A zero here has to say why. "Sales tax $0.00" with no explanation is
          the kind of figure that gets a cashier stopped mid-sale to go and find
          someone who knows (§2.2-10).
        */}
        <Row
          label="Sales tax"
          value={money(sale.sale.taxCents)}
          explain={
            sale.sale.taxExempt ? (
              <Explain label="the zero sales tax">
                No tax charged:{' '}
                {sale.customer === null ? 'this customer' : customerName(sale.customer)} is on file
                as tax exempt.
              </Explain>
            ) : undefined
          }
        />

        <div className="grand">
          <span className="grand__label">Total{dualPricing ? ' — cash' : ''}</span>
          <span className="grand__amount money">{money(sale.sale.totalCents)}</span>
        </div>

        {dualPricing ? (
          <div className="grand__card money">
            Card price: <strong>{money(sale.sale.cardTotalCents!)}</strong>
          </div>
        ) : null}

        {/*
          An exchange is two documents and one conversation. This panel used to
          describe only the document, so a $73.80 credit against a $116.00
          replacement showed "$116.00" here while the banner said "$42.20 left
          to collect" — and the operator had to work out which of the two the
          customer owed. Every derived figure explains itself (§2.2-10), so the
          credit is a line and the difference is the figure at the bottom.
        */}
        {showsCredit ? (
          <>
            <Row
              label={
                creditApplied === true ? 'Exchange credit' : 'Exchange credit — not yet applied'
              }
              value={`−${money(Math.abs(credit))}`}
              tone="success"
            />
            <div className="grand">
              <span className="grand__label">
                {stillDue >= 0 ? 'Left to collect' : 'To give back'}
              </span>
              <span className="grand__amount money">{money(Math.abs(stillDue))}</span>
            </div>
          </>
        ) : null}
      </div>
    </Card>
  );
}

function Row({
  label,
  value,
  tone,
  explain,
}: {
  label: string;
  value: string;
  tone?: 'success';
  explain?: React.ReactNode;
}) {
  return (
    <div className={`totals-row${tone === 'success' ? ' totals-row--success' : ''}`}>
      <span>
        {label}
        {explain}
      </span>
      <span className="money">{value}</span>
    </div>
  );
}

// ---------------------------------------------------------------------------

/**
 * Close the drawer at the end of a shift (SCOPE §8.7, §10.1).
 *
 * A blind count: the figure the drawer should hold is shown only after the
 * cash has been counted and entered, because a count made while looking at
 * the answer tends to find it. Then counted against expected, and the over or
 * short — the three numbers End of day reports for the shift.
 */
function CloseDrawerDialog({
  drawerSessionId,
  onClose,
}: {
  drawerSessionId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [counted, setCounted] = useState('');
  const [notes, setNotes] = useState('');
  const parsed = tryParseMoney(counted);

  const [needsApproval, setNeedsApproval] = useState(false);

  const close = useMutation({
    mutationFn: (override?: ManagerOverride) =>
      api.register.closeDrawer.mutate({
        drawerSessionId,
        countedCents: parsed ?? 0,
        notes: notes.trim() === '' ? null : notes.trim(),
        ...(override === undefined ? {} : { override }),
      }),
    // Somebody else's drawer: a manager's PIN, not a dead end (2026-09-25).
    onError: (error) => {
      if (isOverridable(error)) setNeedsApproval(true);
    },
    onSuccess: () => {
      setNeedsApproval(false);
      recordBreadcrumb('register', 'closed the drawer');
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
    },
  });

  const finish = () => {
    // The register asks again, and finds no drawer: it offers to open one.
    void queryClient.invalidateQueries({ queryKey: ['register'] });
    onClose();
  };

  if (close.data !== undefined) {
    const overShort = close.data.overShortCents ?? 0;
    return (
      <Modal
        open
        title="Drawer closed"
        onClose={finish}
        primaryAction={
          <Button variant="primary" onClick={finish}>
            Done
          </Button>
        }
      >
        <dl className="figures">
          <div>
            <dt>Counted</dt>
            <dd className="money">{money(close.data.countedCents ?? 0)}</dd>
          </div>
          <div>
            <dt>Expected</dt>
            <dd className="money">{money(close.data.expectedCents ?? 0)}</dd>
          </div>
          <div>
            <dt>{overShort === 0 ? 'Balanced' : overShort > 0 ? 'Over' : 'Short'}</dt>
            <dd className="money">{money(Math.abs(overShort))}</dd>
          </div>
        </dl>
        <p className="text-secondary">
          End of day in Reports has the whole shift. The next sale on this register opens a new
          drawer.
        </p>
      </Modal>
    );
  }

  return (
    <>
      <Modal
        open
        title="Close the drawer"
        description="Count the cash in the drawer — float included — and enter the total."
        onClose={onClose}
        secondaryAction={
          <Button variant="ghost" onClick={onClose}>
            Not yet
          </Button>
        }
        primaryAction={
          <Button
            variant="primary"
            busy={close.isPending}
            {...(parsed === null ? { disabledReason: 'Enter the cash counted' } : {})}
            onClick={() => close.mutate(undefined)}
          >
            Close the drawer
          </Button>
        }
      >
        <div className="field">
          <label className="field__label" htmlFor="close-counted">
            Cash counted
          </label>
          <input
            id="close-counted"
            className="input money"
            inputMode="decimal"
            autoFocus
            value={counted}
            onChange={(event) => setCounted(sanitiseAmount(event.target.value))}
          />
        </div>
        <div className="field">
          <label className="field__label" htmlFor="close-notes">
            Notes <span className="text-secondary">Optional</span>
          </label>
          <input
            id="close-notes"
            className="input"
            maxLength={500}
            placeholder="Anything the next person should know"
            value={notes}
            onChange={(event) => setNotes(event.target.value)}
          />
        </div>
        {close.isError && !isOverridable(close.error) ? (
          <Callout tone="danger">{errorMessage(close.error)}</Callout>
        ) : null}
      </Modal>
      {needsApproval ? (
        <ManagerApproval
          title="Manager approval needed"
          because="This drawer was opened by somebody else. A manager approves counting it out."
          busy={close.isPending}
          error={close.isError ? approvalRefusal(close.error) : null}
          onCancel={() => setNeedsApproval(false)}
          onApprove={(override) => close.mutate(override)}
        />
      ) : null}
    </>
  );
}

// ---------------------------------------------------------------------------

/**
 * Cash in or out of the drawer without a sale (SCOPE §8.7).
 *
 * `drawer_movements` has existed since the drawer did, `closeDrawer` has always
 * subtracted it from expected cash, and end-of-day has always reported it — and
 * **nothing ever wrote a row**. So the drops figure was structurally zero, and a
 * manager taking $500 to the safe made the drawer read $500 short with nothing
 * able to say why. A count nobody can explain is a count staff stop believing.
 *
 * The reason is required on anything that moves money, because a $500 line with
 * no reason is only marginally better than no line at all.
 */
const DRAWER_ACTIONS = [
  { kind: 'drop' as const, label: 'Cash drop', hint: 'To the safe, out of the drawer' },
  { kind: 'pay_out' as const, label: 'Pay out', hint: 'Petty cash — a delivery, a taxi' },
  { kind: 'pay_in' as const, label: 'Pay in', hint: 'Float added, or cash returned' },
  { kind: 'no_sale' as const, label: 'No sale', hint: 'Open it, and say why' },
];

function DrawerMovementDialog({
  drawerSessionId,
  onClose,
}: {
  drawerSessionId: string;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [kind, setKind] = useState<(typeof DRAWER_ACTIONS)[number]['kind']>('drop');
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [override, setOverride] = useState<ManagerOverride | null>(null);
  const [done, setDone] = useState(false);

  const movesMoney = kind !== 'no_sale';
  const parsed = movesMoney ? tryParseMoney(amount) : 0;

  const movements = useQuery({
    queryKey: ['register', 'drawerMovements', drawerSessionId],
    queryFn: () => api.register.drawerMovements.query({ drawerSessionId }),
  });

  const record = useMutation({
    mutationFn: () =>
      api.register.drawerMovement.mutate({
        drawerSessionId,
        kind,
        amountCents: parsed ?? 0,
        reason: reason.trim() === '' ? undefined : reason.trim(),
        ...(override === null ? {} : { override }),
      }),
    onSuccess: () => {
      recordBreadcrumb('register', `recorded a ${kind}`);
      void queryClient.invalidateQueries({ queryKey: ['register', 'drawerMovements'] });
      void queryClient.invalidateQueries({ queryKey: ['reports'] });
      setDone(true);
    },
  });

  if (isOverridable(record.error)) {
    return (
      <ManagerApproval
        title="Manager approval needed"
        because={approvalRefusal(record.error)}
        busy={record.isPending}
        onCancel={() => record.reset()}
        onApprove={(approval) => {
          setOverride(approval);
          record.mutate();
        }}
      />
    );
  }

  if (done) {
    return (
      <Modal
        open
        title="Recorded"
        onClose={onClose}
        primaryAction={
          <Button variant="primary" onClick={onClose}>
            Back to the sale
          </Button>
        }
      >
        <Callout tone="success">
          {movesMoney
            ? `${money(parsed ?? 0)} recorded. The drawer expects that much less at close, so the count will still balance.`
            : 'Logged. Nothing moved.'}
        </Callout>
      </Modal>
    );
  }

  return (
    <Modal
      open
      title="Drawer"
      description="Money in or out without a sale. Recorded against this drawer, so the count at close still adds up."
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={record.isPending}
          disabledReason={
            movesMoney && (parsed === null || parsed <= 0)
              ? 'Enter how much is moving'
              : movesMoney && reason.trim() === ''
                ? 'Say what it is for'
                : undefined
          }
          onClick={() => record.mutate()}
        >
          Record{movesMoney && parsed !== null && parsed > 0 ? ` ${money(parsed)}` : ''}
        </Button>
      }
    >
      <div className="field">
        <span className="field__label">What is happening?</span>
        <div className="chips">
          {DRAWER_ACTIONS.map((action) => (
            <button
              key={action.kind}
              type="button"
              className={`chip${kind === action.kind ? ' chip--on' : ''}`}
              aria-pressed={kind === action.kind}
              onClick={() => setKind(action.kind)}
            >
              {action.label}
            </button>
          ))}
        </div>
        <p className="field__hint">{DRAWER_ACTIONS.find((action) => action.kind === kind)?.hint}</p>
      </div>

      {movesMoney ? (
        <div className="field">
          <label className="field__label" htmlFor="drawer-amount">
            How much?
          </label>
          <input
            id="drawer-amount"
            className="input money"
            inputMode="decimal"
            autoFocus
            value={amount}
            onChange={(event) => setAmount(sanitiseAmount(event.target.value))}
          />
        </div>
      ) : null}

      <div className="field">
        <label className="field__label" htmlFor="drawer-reason">
          {movesMoney ? 'What is it for?' : 'Why was it opened?'}
          {movesMoney ? '' : ' '}
          {movesMoney ? null : <span className="text-secondary">Optional</span>}
        </label>
        <input
          id="drawer-reason"
          className="input"
          maxLength={200}
          placeholder={kind === 'drop' ? 'To the safe' : 'Window cleaner'}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </div>

      {(movements.data?.length ?? 0) > 0 ? (
        <div className="field">
          <span className="field__label">Already on this drawer</span>
          <ul className="tender__taken">
            {movements.data?.map((movement) => (
              <li key={movement.id}>
                <span>
                  {DRAWER_ACTIONS.find((action) => action.kind === movement.kind)?.label ??
                    movement.kind}
                  {movement.reason === null ? '' : ` · ${movement.reason}`}
                </span>
                <span className="money">
                  {movement.amountCents === 0 ? '—' : money(movement.amountCents)}
                </span>
              </li>
            ))}
          </ul>
        </div>
      ) : null}

      {record.isError && !isOverridable(record.error) ? (
        <Callout tone="danger">{errorMessage(record.error)}</Callout>
      ) : null}
    </Modal>
  );
}

/** Every way this store takes money at the counter (SCOPE §8.2, §8.4). */
type TenderMethod = 'cash' | 'card' | 'gift_card' | 'store_credit' | 'check';

/**
 * Order is deliberate: the two that carry almost every sale come first, so the
 * common case needs no reading at all and the rare ones are still one press.
 */
const TENDER_METHODS: readonly TenderMethod[] = [
  'cash',
  'card',
  'gift_card',
  'store_credit',
  'check',
];

/**
 * What pressing the tender's primary action does, said as the action.
 *
 * It used to read "Recorded · $90.00" *before* it was pressed, and two review
 * runs read that as a status: the payment looked recorded, Pay cash was still
 * lit, and the register looked stuck (decision 114). A control says exactly
 * what happens, in the imperative; "recorded" is true on the completion
 * screen and nowhere earlier (DESIGN-DNA Part 5). Cash keeps its "Exact"
 * wording because there the figure *is* the instruction.
 */
function tenderActionLabel(method: TenderMethod, amountCents: number): string {
  const amount = money(amountCents);
  switch (method) {
    case 'cash':
      return `Exact · ${amount}`;
    case 'card':
      return `Record ${amount} on card`;
    case 'check':
      return `Record ${amount} by check`;
    case 'gift_card':
      return `Take ${amount} from gift card`;
    case 'store_credit':
      return `Use ${amount} of store credit`;
  }
}

function TenderPanel({
  sale,
  registerId,
  cards,
  cardPress,
  onCardPressUsed,
  method,
  onMethod,
  onRefundRequested,
  onSaleChanged,
  onCompleted,
}: {
  sale: SaleView;
  registerId: string;
  /** Whether this register takes cards through BlueSage (decisions 229–231). */
  cards: { enabled: boolean; sandbox: boolean };
  /** *Pay card* was just pressed: send at once (used up by the panel). */
  cardPress: boolean;
  onCardPressUsed: () => void;
  method: TenderMethod;
  onMethod: (method: TenderMethod) => void;
  onRefundRequested: () => void;
  onSaleChanged: (sale: SaleView) => void;
  onCompleted: (number: string, changeCents: number, kind: string) => void;
}) {
  /*
   * Which way they are actually paying (SCOPE §8.4).
   *
   * The action bar starts the panel on cash or card, because those are the two
   * that need to be one press. Gift card, store credit and check live *here*
   * rather than on the bar: they are chosen at the moment of paying, and four
   * more buttons at the bottom of the register would take the decision that
   * happens hundreds of times a day past the point where it stays fast
   * (§2.2-2). The server has accepted all five kinds since the draw-down was
   * built — there was simply no way to reach three of them.
   *
   * Held by the sale, not here: a copy taken when the panel opened went stale
   * the moment anything else chose a method (FB-1121).
   */
  /*
   * Keyed on the total as well as the tenders, and never served from cache: a
   * discount or a removed line between two openings of this panel left it
   * offering the old amount, and a check for that amount was refused
   * (2026-09-30). What is owed is asked for every time the panel opens.
   */
  const settlement = useQuery({
    queryKey: [
      'register',
      'settlement',
      sale.sale.id,
      sale.tenders.length,
      sale.sale.totalCents,
      sale.sale.taxExempt,
    ],
    queryFn: () => api.register.settlement.query({ saleId: sale.sale.id }),
    staleTime: 0,
  });

  // Straight from the server. The client used to derive the card amount here
  // and got it wrong on refunds by a factor of thirty thousand.
  const due =
    settlement.data == null
      ? sale.sale.totalCents
      : method === 'card'
        ? settlement.data.dueForCard
        : settlement.data.dueForCash;

  /**
   * Until the server has said what is owed, there is no amount to offer.
   *
   * `due` falls back to the sale's **cash** total while that query is in
   * flight, and that figure is wrong twice over. On a card it is the cash
   * price, so an operator who presses before the answer lands charges the cash
   * total on a card and the shop eats the whole programme rate (§8.2). On a
   * part-paid sale it is the entire total rather than the balance. Neither ever
   * surfaces as a drawer variance, because a tender *was* recorded for exactly
   * what was run — it is simply quietly less money than the sale was worth,
   * hundreds of times a day, by the operators who are fastest.
   *
   * Found by the browser suite on a loaded machine, where the query took long
   * enough for the press to win the race. On a quick laptop it never showed.
   *
   * "Not yet known" is not the same as "the total" — so nothing is offered and
   * nothing can be pressed until it is known (§2.2-7, §2.2-12).
   */
  const settling = settlement.data == null;

  const [typed, setTyped] = useState('');
  const [cardNumber, setCardNumber] = useState('');
  const isRefund = sale.sale.originalSaleId !== null;
  const customerId = sale.customer?.id ?? null;

  /** Prepaid value has to be looked at before it is spent, never assumed. */
  /*
   * The number once typing has paused. It looked a balance up on every key
   * after the fourth, and each half-typed number was a "No gift card"
   * flashed as the reason and logged as an error (FB-1062). A scanner
   * finishes well inside the pause; a person typing gets one lookup.
   */
  /*
   * A scanner ends with Enter, and so can a person: either looks the card up
   * at once. Otherwise only a real pause does — 300 ms was shorter than the
   * gap between keys of someone reading sixteen digits off a receipt, so a
   * half-typed number came back "No gift card with that number" (FB-1106).
   */
  const [settledNumber, setSettledNumber] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setSettledNumber(cardNumber.trim()), GIFT_CARD_PAUSE_MS);
    return () => clearTimeout(timer);
  }, [cardNumber]);
  const cardTyping = settledNumber !== cardNumber.trim();

  const giftBalance = useQuery({
    queryKey: ['register', 'giftCardBalance', settledNumber],
    enabled: method === 'gift_card' && !isRefund && settledNumber.length >= 4,
    retry: false,
    queryFn: () => api.register.giftCardBalance.query({ number: settledNumber }),
  });

  const creditBalance = useQuery({
    queryKey: ['register', 'storeCreditBalance', customerId],
    enabled: method === 'store_credit' && !isRefund && customerId !== null,
    queryFn: () => api.register.storeCreditBalance.query({ customerId: customerId! }),
  });

  /**
   * The most this method can cover right now.
   *
   * A card with $20 on it against a $90 basket pays $20 and leaves $70 — it is
   * not an error and it must not be one, because splitting across a gift card
   * and something else is most of what gift cards are used for.
   */
  const available =
    method === 'gift_card' && !isRefund
      ? (giftBalance.data?.balanceCents ?? null)
      : method === 'store_credit' && !isRefund
        ? (creditBalance.data?.balanceCents ?? null)
        : null;

  const payable = available === null ? due : Math.min(available, due);

  /** A card recorded by hand while BlueSage is on needs a manager (decision 230). */
  const [manualApproval, setManualApproval] = useState<number | null>(null);
  const tender = useMutation({
    mutationFn: (input: number | { amountCents: number; override: ManagerOverride }) =>
      api.register.tender.mutate({
        saleId: sale.sale.id,
        kind: method,
        amountCents: typeof input === 'number' ? input : input.amountCents,
        ...(typeof input === 'number' ? {} : { override: input.override }),
        ...(method === 'gift_card' ? { giftCardNumber: cardNumber.trim() } : {}),
      }),
    onSuccess: (result) => {
      setManualApproval(null);
      onSaleChanged(result.sale);
      if (result.completed && result.saleNumber != null) {
        recordBreadcrumb('register', `completed ${result.saleNumber}`);
        onCompleted(result.saleNumber, result.settlement.changeDueCents, method);
      } else {
        setTyped('');
        setCardNumber('');
      }
    },
    onError: (error, input) => {
      if (method === 'card' && cards.enabled && isOverridable(error)) {
        setManualApproval(typeof input === 'number' ? input : input.amountCents);
      }
    },
  });
  const throughBlueSage = method === 'card' && cards.enabled;
  const refreshSale = () =>
    void api.register.getSale.query({ saleId: sale.sale.id }).then((view) => onSaleChanged(view));

  // Quick amounts: exact, then the next round notes above it. Recognition over
  // arithmetic — the cashier matches what is in their hand (§2.2-3).
  const quickAmounts = useMemo(() => {
    if (method !== 'cash' || isRefund) return [];
    const rounded = new Set<number>();
    for (const note of [500, 1000, 2000, 5000, 10_000]) {
      const up = Math.ceil(due / note) * note;
      if (up > due) rounded.add(up);
    }
    return [...rounded].sort((a, b) => a - b).slice(0, 3);
  }, [due, method, isRefund]);

  const typedCents = typed === '' ? null : tryParseMoney(typed);
  /*
   * Why a typed amount cannot be taken yet, or undefined when it can. Cash may
   * exceed what is owed (the difference is change); a card or a check cannot
   * give change, so the ceiling is what is owed on that basis.
   */
  const typedReason =
    typed === ''
      ? undefined
      : typedCents == null
        ? 'That is not an amount'
        : typedCents <= 0
          ? 'Enter more than zero'
          : method !== 'cash' && typedCents > due
            ? `That is more than is owed — the most this can take is ${money(due)}`
            : undefined;
  const livePayments = sale.tenders.filter((tender) => tender.voidedAt === null);
  /*
   * An exchange credit is not refundable — there is no money behind it to give
   * back. Offering "Refund payment" against it invited the operator to hand
   * over the value of the item being swapped *and* let the customer keep the
   * replacement (§8.5). Unwinding an exchange is `Just refund instead`.
   */
  const refundable = livePayments.filter((tender) => tender.kind !== 'exchange_credit');

  const blocked = settling
    ? 'Working out what is owed…'
    : method === 'store_credit' && customerId === null
      ? 'Attach a customer — store credit belongs to a person'
      : method === 'gift_card' && !isRefund && cardNumber.trim() === ''
        ? 'Scan the gift card'
        : method === 'gift_card' && !isRefund && cardTyping
          ? 'Finish the number, then press Enter'
          : method === 'gift_card' && !isRefund && giftBalance.isError
            ? errorMessage(giftBalance.error)
            : available !== null && available <= 0
              ? 'There is nothing left on this to spend'
              : undefined;

  return (
    <Card title={TENDER_LABELS[method]}>
      <div className="tender">
        <div className="tender__due">
          <span className="label-micro">{isRefund ? 'Amount to refund' : 'Amount due'}</span>
          {/* A figure that is not yet known is shown as unknown, never as the
              nearest number to hand — the operator reads this one out loud. */}
          <span className="tender__amount money">{settling ? '—' : money(Math.abs(due))}</span>
        </div>

        {/*
          Every way this store takes money, in one row, with the two that carry
          the sale first. Recognition over recall (§2.2-3): the operator picks
          what the customer just handed them rather than remembering which menu
          hides gift cards.
        */}
        <div className="chips tender__methods">
          {TENDER_METHODS.map((entry) => (
            <button
              key={entry}
              type="button"
              className={`chip${method === entry ? ' chip--on' : ''}`}
              aria-pressed={method === entry}
              onClick={() => {
                onMethod(entry);
                setTyped('');
              }}
            >
              {TENDER_LABELS[entry]}
            </button>
          ))}
        </div>

        {throughBlueSage ? (
          <>
            {isRefund ? (
              <ReturnToCardPanel
                saleId={sale.sale.id}
                registerId={registerId}
                dueCents={settling ? null : due}
                onSaleChanged={refreshSale}
                onCompleted={(number) => onCompleted(number, 0, 'card')}
              />
            ) : null}
            <TerminalCardPanel
              saleId={sale.sale.id}
              registerId={registerId}
              dueCents={settling ? null : due}
              isRefund={isRefund}
              sandbox={cards.sandbox}
              autoSend={cardPress}
              onAutoSent={onCardPressUsed}
              onSaleChanged={refreshSale}
              onCompleted={(number) => onCompleted(number, 0, 'card')}
            />
            <More label="The terminal is not working">
              <p className="text-secondary">
                Run the card on the terminal by hand, then record it here. A manager approves it,
                and End of Day lists it to match against the terminal’s batch.
              </p>
              <Button
                variant="secondary"
                busy={tender.isPending}
                disabledReason={blocked}
                onClick={() => tender.mutate(payable)}
              >
                {isRefund
                  ? `Record ${money(Math.abs(payable))} refunded by hand`
                  : `Record ${money(payable)} run by hand`}
              </Button>
            </More>
            {manualApproval !== null ? (
              <ManagerApproval
                title="Manager approval needed"
                because="A card run on the terminal by hand is for when the terminal will not take it from Shekko."
                busy={tender.isPending}
                error={tender.isError ? approvalRefusal(tender.error) : null}
                onCancel={() => setManualApproval(null)}
                onApprove={(override) => tender.mutate({ amountCents: manualApproval, override })}
              />
            ) : null}
          </>
        ) : method === 'card' ? (
          <p className="text-secondary">
            {isRefund
              ? `Refund ${money(Math.abs(due))} on the terminal, then record it here.`
              : `Run ${money(due)} on the terminal, then record it here.`}
          </p>
        ) : null}

        {method === 'gift_card' ? (
          <div className="field">
            <label className="field__label" htmlFor="tender-gift-number">
              {isRefund ? 'Card to load' : 'Gift card number'}
            </label>
            <input
              id="tender-gift-number"
              className="input code"
              inputMode="numeric"
              autoFocus
              placeholder="Scan the card"
              value={cardNumber}
              onChange={(event) => setCardNumber(event.target.value)}
              onKeyDown={(event) => {
                if (event.key !== 'Enter') return;
                event.preventDefault();
                setSettledNumber(cardNumber.trim());
              }}
            />
            {giftBalance.data != null && !isRefund ? (
              <p className="field__hint">
                Balance <strong className="num">{money(giftBalance.data.balanceCents)}</strong>
                {giftBalance.data.balanceCents < due
                  ? ` — covers part of this sale, leaving ${money(due - giftBalance.data.balanceCents)}.`
                  : '.'}
              </p>
            ) : null}
          </div>
        ) : null}

        {/* Attach them here, without leaving the payment (FB-1058: "should I see a
            form to enter the customer's name?" — it was one press away, elsewhere). */}
        {method === 'store_credit' && !isRefund && customerId === null ? (
          <CustomerOnSale saleId={sale.sale.id} customer={null} onAttached={onSaleChanged} />
        ) : null}
        {method === 'store_credit' && !isRefund ? (
          <p className="text-secondary">
            {customerId === null
              ? 'Store credit belongs to a customer. Attach one to this sale first.'
              : creditBalance.data == null
                ? 'Checking what they have…'
                : `${money(creditBalance.data.balanceCents)} of store credit available.`}
          </p>
        ) : null}

        {throughBlueSage ? null : (
          <Button
            variant="primary"
            size="large"
            busy={tender.isPending}
            disabledReason={blocked}
            onClick={() => tender.mutate(payable)}
          >
            {settling
              ? TENDER_LABELS[method]
              : isRefund
                ? `Give back · ${money(Math.abs(payable))}`
                : tenderActionLabel(method, payable)}
          </Button>
        )}

        {quickAmounts.length > 0 ? (
          <div className="tender__quick">
            {quickAmounts.map((amount) => (
              <button
                key={amount}
                type="button"
                className="tender__quick-button money"
                onClick={() => tender.mutate(amount)}
                // Derived from `due`, so they carry the same staleness the
                // primary action does while the settlement is in flight.
                disabled={tender.isPending || settling}
                // Waiting, and saying so: greyed with no reason was what the
                // every-journey check caught on a slow first sale (2026-10-25)
                // — the same class as the cart's buttons on 2026-10-20.
                aria-busy={tender.isPending || settling ? true : undefined}
              >
                {money(amount)}
              </button>
            ))}
          </div>
        ) : null}

        {/*
          A part of the sale, typed (SCOPE §8.2: cash plus one or more cards).

          Cash has always had this — a customer hands over a round note and
          the rest is change. Card and check gained it on 2026-09-15 (FB-1061,
          decision 114): "fifty on this card, the rest in cash" had no way in,
          because the only amount a card could take was everything still owed.
          The server accepted a partial card amount all along; the screen never
          offered one. Gift card and store credit stay bounded by their balance
          rather than typed, and a refund gives back exactly what is owed.

          A card cannot give change, so more than is owed is refused before the
          press rather than explained afterwards (§2.2-7); cash above the total
          is simply change.
        */}
        {(method === 'cash' || (method === 'card' && !throughBlueSage) || method === 'check') &&
        !isRefund ? (
          <div className="tender__custom">
            <label className="field__label" htmlFor="tender-amount">
              {method === 'cash'
                ? 'Other amount'
                : method === 'card'
                  ? 'Amount on this card'
                  : 'Amount of the check'}
            </label>
            <input
              id="tender-amount"
              className="input money"
              inputMode="decimal"
              placeholder="0.00"
              value={typed}
              // Digits and one decimal point, filtered as they type. Nothing
              // else can be money, so nothing else gets in (§2.2-7 — prevent
              // the error rather than explain it afterwards).
              onChange={(event) => setTyped(sanitiseAmount(event.target.value))}
              onKeyDown={(event) => {
                // Not while one is being recorded, nor before the server has said
                // what is owed: a second Enter recorded a second part payment.
                if (
                  event.key === 'Enter' &&
                  typedCents != null &&
                  typedReason === undefined &&
                  blocked === undefined &&
                  !tender.isPending
                ) {
                  event.preventDefault();
                  tender.mutate(typedCents);
                }
              }}
            />
            <Button
              variant="secondary"
              busy={tender.isPending}
              disabledReason={blocked ?? typedReason}
              onClick={() => {
                if (typedCents != null) tender.mutate(typedCents);
              }}
            >
              {method === 'cash'
                ? `Take ${typedCents == null ? '' : money(typedCents)}`
                : typedCents == null
                  ? method === 'card'
                    ? 'Record on card'
                    : 'Record by check'
                  : tenderActionLabel(method, typedCents)}
            </Button>
          </div>
        ) : null}

        {livePayments.length > 0 ? (
          <ul className="tender__taken">
            {livePayments.map((taken) => (
              <li key={taken.id}>
                <span>{TENDER_LABELS[taken.kind] ?? taken.kind}</span>
                <span className="money">{money(taken.amountCents)}</span>
              </li>
            ))}
          </ul>
        ) : null}

        {tender.isError && !(throughBlueSage && isOverridable(tender.error)) ? (
          <Callout tone="danger">{errorMessage(tender.error)}</Callout>
        ) : null}

        {/* Back to cart is on the bar below, where the Pay buttons were. */}
        {refundable.length > 0 ? (
          <div className="tender__exits">
            <Button variant="danger" onClick={onRefundRequested}>
              Refund payment
            </Button>
          </div>
        ) : null}
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------

function CompletedScreen({
  receipt,
  onNext,
}: {
  receipt: { number: string; changeCents: number; kind: string; saleId: string };
  onNext: () => void;
}) {
  const settings = useReceiptPrinting();
  const printer = useReceiptPrinter();
  const [emailing, setEmailing] = useState(false);

  const detail = useQuery({
    queryKey: ['sales', 'detail', receipt.saleId],
    queryFn: () => api.sales.get.query({ saleId: receipt.saleId }),
  });

  const canPrint = detail.data !== undefined && settings !== null;
  /*
   * What actually happened, read off the document rather than assumed.
   *
   * This screen said "Sale complete · Paid by cash" for a refund as well, which
   * is money going the other way described as though the customer had handed it
   * over — and an exchange came out as "Paid by exchange".
   */
  const isRefund = detail.data?.sale.originalSaleId != null;
  const isExchange = detail.data?.exchange != null;
  const noun = isExchange ? 'Exchange' : isRefund ? 'Refund' : 'Sale';

  const changeOwed = receipt.changeCents > 0;

  /*
   * Print it without being asked (revision item 8).
   *
   * A receipt is what the customer expects to be handed, and making it a press
   * means the busiest moment of the day is the one where it gets skipped. Only
   * when the store has said this till prints silently — see
   * `receiptAutoPrint` in the schema for why that is a setting rather than a
   * default.
   *
   * Once, guarded by a ref rather than by the effect's dependencies: `detail`
   * refetches, `settings` refetches, and either would otherwise reprint a
   * receipt that is already in the customer's hand.
   */
  const autoPrinted = useRef(false);
  useEffect(() => {
    if (autoPrinted.current) return;
    if (!canPrint || settings?.receipt.autoPrint !== true) return;
    autoPrinted.current = true;
    printer.print(detail.data!, settings);
  }, [canPrint, detail.data, settings, printer]);

  /*
   * Move on by itself when there is nothing left to do here (revision item 8).
   *
   * **Never while change is owed.** That is the whole design: a register that
   * clears itself with the drawer open and money still to count out is how a
   * customer leaves short, and it balances perfectly afterwards because the
   * tender was recorded for exactly what was taken. So the timer is not
   * *started* in that case rather than being cancelled if the operator is
   * quick — a race nobody can lose is better than one they usually win.
   *
   * Anything the operator does here means they are not finished, so opening the
   * email dialog stops it too. Off unless the store has set a number.
   */
  const autoAdvanceSeconds = settings?.registerPolicy.completedAutoAdvanceSeconds ?? 0;
  const autoAdvancing = autoAdvanceSeconds > 0 && !changeOwed && !emailing;
  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  useEffect(() => {
    if (!autoAdvancing) {
      setSecondsLeft(null);
      return;
    }

    setSecondsLeft(autoAdvanceSeconds);
    const tick = setInterval(() => {
      setSecondsLeft((left) => {
        if (left === null) return null;
        if (left <= 1) {
          clearInterval(tick);
          onNext();
          return null;
        }
        return left - 1;
      });
    }, 1000);

    return () => clearInterval(tick);
  }, [autoAdvancing, autoAdvanceSeconds, onNext]);

  return (
    <Screen
      title={`${noun} complete`}
      destructiveAction={
        <Button
          variant="ghost"
          disabledReason={canPrint ? undefined : 'Preparing the receipt'}
          onClick={() => printer.print(detail.data!, settings!, true)}
        >
          Gift receipt
        </Button>
      }
      secondaryActions={
        <>
          {/*
            Emailing a receipt lived only on the Sales screen, which is where
            you go a day later. *Here* is the one moment the customer is
            standing in front of you to give you an address — and a receipt
            emailed then is the one that gets kept, which is what makes a
            no-receipt return findable later (§8.6, §9.1).
          */}
          <Button
            variant="ghost"
            disabledReason={detail.data === undefined ? 'Preparing the receipt' : undefined}
            onClick={() => setEmailing(true)}
          >
            Email receipt
          </Button>
          <Button
            variant="secondary"
            size="large"
            disabledReason={canPrint ? undefined : 'Preparing the receipt'}
            onClick={() => printer.print(detail.data!, settings!)}
          >
            Print receipt
          </Button>
        </>
      }
      primaryAction={
        <Button variant="primary" size="large" autoFocus onClick={onNext}>
          {/*
            The countdown is on the button rather than in a banner, because the
            button is what it is going to press. A timer the operator cannot see
            is a screen that vanishes for no reason they can name (§2.2-6).
          */}
          {secondsLeft === null ? 'Next customer' : `Next customer · ${secondsLeft}s`}
        </Button>
      }
    >
      {printer.frame}

      {/*
        Change first, and the largest thing on the screen (revision item 8).
        It was in a card *below* the completion banner, at the same weight as
        everything else — and it is the one number that has to leave the
        operator's mouth before the customer leaves the counter. Von Restorff
        (§2.1): one focal point, earned by size, and on this screen it is this.
      */}
      {changeOwed ? (
        <Card>
          {/*
            A live region, because this is the definition of a status message:
            it appears when the sale completes, it is the one thing that has to
            be acted on before the customer leaves, and somebody working the
            counter with a screen reader should hear it rather than have to go
            looking for it (§2.1 — the accessibility tree is real).
          */}
          <div className="change" role="status">
            <span className="label-micro">Change due</span>
            <span className="change__amount money">{money(receipt.changeCents)}</span>
          </div>
        </Card>
      ) : null}

      {/* Explicit success, never silent (§2.2-6). */}
      <Callout tone="success" title={`${noun} ${receipt.number} complete`}>
        {isExchange
          ? 'Settled against the return — only the difference moved.'
          : isRefund
            ? `Refunded to ${TENDER_LABELS[receipt.kind]?.toLowerCase() ?? receipt.kind}.`
            : `Paid by ${TENDER_LABELS[receipt.kind]?.toLowerCase() ?? receipt.kind}.`}
        {settings?.receipt.autoPrint === true ? ' The receipt is printing.' : ''}
      </Callout>

      {/*
        The receipt, before it is printed or emailed.

        Every control on this screen sends this document somewhere, and until
        now none of them showed it — so "print" and "email" were both acts of
        faith, and a receipt with the wrong store name or a missing line was
        discovered by a customer. It is the same `ReceiptDocument` the printer
        and the email render, never a second version of it: one document, one
        rendering, which is the rule the cash/card price bug taught (decision
        39).
      */}
      {canPrint ? (
        <Card title="Receipt">
          <div className="receipt-preview">
            <ReceiptDocument detail={detail.data!} settings={settings!} />
          </div>
        </Card>
      ) : null}

      {emailing ? (
        <EmailReceiptDialog
          saleId={receipt.saleId}
          defaultEmail={detail.data?.customer?.email ?? ''}
          attachedCustomerId={detail.data?.customer?.id ?? null}
          onClose={() => setEmailing(false)}
        />
      ) : null}
    </Screen>
  );
}

/**
 * The state of an exchange, on whichever half is open (SCOPE §8.5).
 *
 * Two documents are harder to hold in your head than one, so the screen says
 * plainly which half you are on, what the credit is worth, and what is left to
 * move — and offers the one press that takes you to the half where the money
 * actually is (§2.2-9, §2.2-11: never a state with no way on).
 */
function ExchangeBanner({
  view,
  isRefundHalf,
  creditApplied,
  switching,
  onSwitch,
  reverting,
  onRevert,
}: {
  view: {
    refundSaleId: string;
    replacementSaleId: string;
    credit: { amountCents: number; cashEquivalentCents: number };
    /** What the return is worth in its own right, before anything offsets it. */
    refundValueCents: number;
    dueFromCustomerCents: number;
    dueToCustomerCents: number;
  } | null;
  isRefundHalf: boolean;
  creditApplied: boolean;
  switching: boolean;
  onSwitch: (saleId: string) => void;
  reverting: boolean;
  /**
   * Go back to a plain return. Null once real money has been taken on the new
   * sale — that has to be refunded first, and the tender screen does it.
   */
  onRevert: (() => void) | null;
}) {
  if (view === null) return null;

  const otherHalf = isRefundHalf ? view.replacementSaleId : view.refundSaleId;

  /*
   * Offered in every unfinished state, not just the first one. "I'll just take
   * the refund" is said as often *after* looking at the replacements as before,
   * and a decision that can only be reversed at the start is one the operator
   * has to get right before the customer has.
   */
  const revert =
    onRevert === null ? null : (
      <Button variant="ghost" busy={reverting} onClick={onRevert}>
        Just refund instead
      </Button>
    );

  if (!creditApplied) {
    return (
      <Callout tone="info" title="Exchange">
        {isRefundHalf ? (
          <>
            These are the items coming back. Scan what the customer is taking on the other half, and
            the two settle against each other so only the difference moves.
            <div className="register__exchange-actions">
              <Button variant="secondary" busy={switching} onClick={() => onSwitch(otherHalf)}>
                Go to what they are taking
              </Button>
              {revert}
            </div>
          </>
        ) : (
          <>
            {/*
              What the *return* is worth, not what the credit currently covers.
              The credit is `min(refund, replacement)`, so it reads $0.00 until
              something has been scanned — and the first thing an operator saw
              on every exchange was "the return is worth $0.00", which is the
              one number guaranteed to be wrong.
            */}
            Scan what the customer is taking. The return is worth{' '}
            <strong className="num">{money(view.refundValueCents)}</strong> — credited at the price
            they paid, so a like-for-like swap is even.
            <div className="register__exchange-actions">
              <Button variant="secondary" busy={switching} onClick={() => onSwitch(otherHalf)}>
                Back to what they are returning
              </Button>
              {revert}
            </div>
          </>
        )}
      </Callout>
    );
  }

  if (view.dueFromCustomerCents > 0) {
    return (
      <Callout tone="warning" title="Exchange — the customer owes the difference">
        <strong className="num">{money(view.credit.amountCents)}</strong> credited.{' '}
        <strong className="num">{money(view.dueFromCustomerCents)}</strong> left to collect.
        {/*
          The way to that money is the primary action bottom-right, where every
          other "do the thing" lives (§2.2-1). It used to be a second button in
          here as well, which is two focal points for one action.
        */}
        <div className="register__exchange-actions">{revert}</div>
      </Callout>
    );
  }

  if (view.dueToCustomerCents > 0) {
    return (
      <Callout tone="warning" title="Exchange — the shop owes the difference">
        <strong className="num">{money(view.credit.amountCents)}</strong> credited.{' '}
        <strong className="num">{money(view.dueToCustomerCents)}</strong> to give back
        {isRefundHalf ? '' : ', on the return half'}.
        <div className="register__exchange-actions">{revert}</div>
      </Callout>
    );
  }

  return (
    <Callout tone="success" title="Exchange — an even swap">
      <strong className="num">{money(view.credit.amountCents)}</strong> credited against{' '}
      {money(view.credit.amountCents)} taken. Nothing to collect and nothing to give back.
    </Callout>
  );
}

/**
 * Selling a gift card (SCOPE §8.4).
 *
 * The amount is the whole form. A card number is asked for only when the shop
 * uses pre-printed stock — leave it blank and one is minted, which is what a
 * shop printing codes on the receipt does. Supporting both is why open decision
 * §16-9 is a purchasing question rather than a build one.
 */
function GiftCardDialog({
  saleId,
  onClose,
  onAdded,
}: {
  saleId: string;
  onClose: () => void;
  onAdded: (view: SaleView) => void;
}) {
  const [amount, setAmount] = useState('');
  const [number, setNumber] = useState('');
  /*
   * Scanning is the default, and *not* scanning is the deliberate choice.
   *
   * It was the other way round: the number lived behind a disclosure, so the
   * ordinary path minted a number nobody had. The physical card in the
   * customer's hand then had no relationship to the value that was sold — it
   * could not be looked up, checked at the counter, or swiped, and the only
   * record was a code on a receipt that goes in the bin.
   */
  const [printed, setPrinted] = useState(false);
  const parsed = tryParseMoney(amount);
  const scanned = number.trim();

  const add = useMutation({
    mutationFn: () =>
      api.register.addGiftCard.mutate({
        saleId,
        amountCents: parsed ?? 0,
        number: printed || scanned === '' ? null : scanned,
      }),
    onSuccess: (view) => {
      recordBreadcrumb('register', 'sold a gift card');
      onAdded(view);
    },
  });

  return (
    <Modal
      open
      title="Sell a gift card"
      description="It goes on this sale like any other line, and loads when the sale is paid for."
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={add.isPending}
          disabledReason={
            parsed === null || parsed <= 0
              ? 'Enter how much goes on the card'
              : !printed && scanned === ''
                ? 'Scan the card the customer is buying'
                : undefined
          }
          onClick={() => add.mutate()}
        >
          Add {parsed === null ? '' : money(parsed)}
        </Button>
      }
    >
      <div className="field">
        <label className="field__label" htmlFor="gift-amount">
          How much?
        </label>
        <input
          id="gift-amount"
          className="input money"
          inputMode="decimal"
          autoFocus
          value={amount}
          onChange={(event) => setAmount(event.target.value)}
        />
      </div>

      {printed ? (
        <div className="field">
          <p className="field__hint">
            A number will be generated and printed on the receipt. The customer has to keep it —
            there is no card to look it up from.
          </p>
          <Button variant="ghost" onClick={() => setPrinted(false)}>
            Scan a card instead
          </Button>
        </div>
      ) : (
        <div className="field">
          <label className="field__label" htmlFor="gift-number">
            Card number
          </label>
          <input
            id="gift-number"
            className="input code"
            inputMode="numeric"
            placeholder="Scan the card"
            value={number}
            onChange={(event) => setNumber(event.target.value)}
          />
          <p className="field__hint">
            Scanning is what ties the plastic in their hand to the value being sold, so it can be
            checked and spent later.
          </p>
          {/* The shop that prints codes on receipts instead still has a way
              through — it is just no longer the accidental default. */}
          <Button variant="ghost" onClick={() => setPrinted(true)}>
            No card — print the code on the receipt
          </Button>
        </div>
      )}

      {add.isError ? <Callout tone="danger">{errorMessage(add.error)}</Callout> : null}
    </Modal>
  );
}

/**
 * Putting a sale on layaway (SCOPE §8.3).
 *
 * The deposit is pre-filled at the store's minimum, because that is the number
 * being confirmed rather than decided (§2.2-5) — and the field says what the
 * minimum is, so a customer offering less gets an answer without a refusal.
 */
function LayawayDialog({
  sale,
  registerId,
  cards,
  onClose,
  onCreated,
}: {
  sale: SaleView;
  registerId: string;
  cards: { enabled: boolean; sandbox: boolean };
  onClose: () => void;
  onCreated: (result: { split: boolean; changeCents: number }) => void;
}) {
  const settings = useReceiptPrinting();

  /*
   * Which items are going away (SCOPE §8.3).
   *
   * Everything, until the operator says otherwise — putting the whole basket
   * away is the common case and must stay a single press. But a customer
   * buying ten things and laying one of them away is ordinary too, and the only
   * way to do it before was to ring the layaway on its own first and remember
   * to start the real sale afterwards.
   */
  const [chosen, setChosen] = useState<ReadonlySet<string>>(
    () => new Set(sale.lines.map((line) => line.id)),
  );

  const selected = sale.lines.filter((line) => chosen.has(line.id));
  const everything = selected.length === sale.lines.length;
  // Net already carries this line's share of any sale discount, so the two
  // halves of a split basket add back up to what the whole one was worth.
  const totalCents = selected.reduce((sum, line) => sum + line.netCents + line.taxCents, 0);

  const minimumCents =
    settings === null ? 0 : Math.round((totalCents * settings.layaway.minDepositPpm) / 1_000_000);

  /*
   * Empty, not pre-filled at the minimum (client, 2026-08-15).
   *
   * The minimum was offered as the default on the reasoning that it is the
   * number being *confirmed* rather than decided (§2.2-5). At this counter it
   * is not: the customer says what they are putting down, and a pre-filled
   * figure is one the operator has to clear before typing — a smart default
   * that costs a keystroke instead of saving one. Worse, it is the one figure
   * nobody should be nudged towards, because the shop would rather hold more.
   *
   * The presets below are the actual shortcuts. A third is their habit; a half
   * and the minimum are the other two anyone reaches for.
   */
  const [amount, setAmount] = useState<string>('');
  const [kind, setKind] = useState<'cash' | 'card'>('cash');
  const [cash, setCash] = useState('');
  const shown = amount;
  const parsed = tryParseMoney(shown);

  /** Rounded to the cent, and never below what the store requires. */
  const preset = (fraction: number): number =>
    Math.max(minimumCents, Math.round(totalCents * fraction));

  /*
   * What was put in the operator's hand, as against what is going on the
   * layaway. Without the distinction a $100 note against a $73.80 deposit was
   * recorded as a $100 deposit, because change is normally worked out against
   * the balance and the balance here is the whole layaway — so the customer
   * left $26.20 short and the drawer balanced anyway.
   */
  const handed = kind === 'cash' && cash.trim() !== '' ? tryParseMoney(cash) : null;
  const changeCents = handed === null || parsed === null ? 0 : Math.max(handed - parsed, 0);

  /*
   * A card deposit through BlueSage (decision 230): taken on the terminal as a
   * part payment on this sale first, then the sale becomes the layaway with it
   * as the deposit — so a declined card never leaves a layaway with nothing
   * down.
   */
  const onTerminal = cards.enabled && kind === 'card';
  const depositAttempt = useCardAttempt(sale.sale.id, onTerminal);
  const depositTaken =
    onTerminal &&
    depositAttempt.data?.status === 'approved' &&
    depositAttempt.data.recorded &&
    depositAttempt.data.saleStatus === 'open';

  const convert = useMutation({
    mutationFn: () =>
      api.register.convertToLayaway.mutate({
        saleId: sale.sale.id,
        lineIds: everything ? null : [...chosen],
        depositCents: onTerminal ? 0 : (parsed ?? 0),
        depositKind: onTerminal ? 'paid' : kind,
        cashTenderedCents: handed,
      }),
    onSuccess: (result) => {
      recordBreadcrumb('register', 'put a sale on layaway');
      onCreated({ split: result.split, changeCents: result.changeCents });
    },
  });

  const tooLittle = parsed !== null && parsed < minimumCents;
  const tooMuch = parsed !== null && parsed > totalCents;
  const shortCash = handed !== null && parsed !== null && handed < parsed;

  return (
    <Modal
      open
      title="Put this on layaway"
      description="The goods come off sale straight away and are held for this customer."
      onClose={onClose}
      secondaryAction={
        <Button variant="ghost" onClick={onClose}>
          Cancel
        </Button>
      }
      primaryAction={
        <Button
          variant="primary"
          busy={convert.isPending}
          disabledReason={
            selected.length === 0
              ? 'Choose at least one item to put away'
              : onTerminal && !everything
                ? 'A deposit taken on the terminal puts the whole sale on layaway'
                : onTerminal && !depositTaken
                  ? 'Take the deposit on the terminal first'
                  : parsed === null
                    ? 'Enter the deposit'
                    : tooLittle
                      ? `This store asks for at least ${money(minimumCents)} down`
                      : tooMuch
                        ? 'That is more than the layaway is worth'
                        : shortCash
                          ? 'That is less cash than the deposit'
                          : undefined
          }
          onClick={() => convert.mutate()}
        >
          {onTerminal
            ? 'Put it on layaway'
            : `Take ${parsed === null ? '' : money(parsed)} deposit`}
        </Button>
      }
    >
      {/*
        Only shown when there is a choice to make. One item on the counter has
        nothing to select, and a list of one with a ticked box beside it is a
        decision the operator has to read before dismissing (§2.2-2).
      */}
      {sale.lines.length > 1 ? (
        <div className="field">
          <span className="field__label">What is going away?</span>
          <ul className="layaway-picker">
            {sale.lines.map((line) => (
              <li key={line.id}>
                <label className="layaway-picker__item">
                  <input
                    type="checkbox"
                    checked={chosen.has(line.id)}
                    onChange={() =>
                      setChosen((current) => {
                        const next = new Set(current);
                        if (next.has(line.id)) next.delete(line.id);
                        else next.add(line.id);
                        return next;
                      })
                    }
                  />
                  <span className="layaway-picker__name">
                    {line.quantity > 1 ? `${line.quantity} × ` : ''}
                    {line.description}
                  </span>
                  <span className="money">{money(line.netCents + line.taxCents)}</span>
                </label>
              </li>
            ))}
          </ul>
          <p className="field__hint">
            {everything
              ? 'The whole sale goes on layaway.'
              : `${selected.length} of ${sale.lines.length} items — the rest stay on the sale to pay for now.`}
          </p>
        </div>
      ) : null}

      <div className="field">
        <label className="field__label" htmlFor="layaway-deposit">
          Deposit
        </label>
        <input
          id="layaway-deposit"
          className="input money"
          inputMode="decimal"
          autoFocus
          placeholder="0.00"
          value={shown}
          onChange={(event) => setAmount(event.target.value)}
        />

        {/*
          One press for the three figures anybody actually asks for. A third is
          the client's own habit; the minimum is the store's policy and is what
          the field used to be pre-filled with. Each is floored at the minimum,
          so a preset can never propose something the server will refuse
          (§2.2-7 — offer nothing that cannot be accepted).
        */}
        <div className="chips layaway__presets">
          {[
            ['A third', preset(1 / 3)],
            ['Half', preset(1 / 2)],
            ['Minimum', minimumCents],
          ].map(([label, cents]) => (
            <button
              key={label as string}
              type="button"
              className="chip"
              onClick={() => setAmount(((cents as number) / 100).toFixed(2))}
            >
              {label as string} · {money(cents as number)}
            </button>
          ))}
        </div>

        <p className="field__hint">
          {money(minimumCents)} minimum on a {money(totalCents)} layaway
          {settings === null ? '' : ` · ${settings.layaway.durationDays} days to pay`}. The balance
          is {money(Math.max(totalCents - (parsed ?? 0), 0))}.
        </p>
      </div>

      <div className="field">
        <span className="field__label">Paid by</span>
        <div className="chips">
          <button
            type="button"
            className={`chip${kind === 'cash' ? ' chip--on' : ''}`}
            aria-pressed={kind === 'cash'}
            onClick={() => setKind('cash')}
          >
            Cash
          </button>
          <button
            type="button"
            className={`chip${kind === 'card' ? ' chip--on' : ''}`}
            aria-pressed={kind === 'card'}
            onClick={() => setKind('card')}
          >
            Card
          </button>
        </div>
      </div>

      {/*
        The same cash flow as any other payment: what they handed over, and what
        goes back. Blank means they handed the deposit exactly, which is the
        common case and costs nothing to leave alone (§2.2-5).
      */}
      {kind === 'cash' ? (
        <div className="field">
          <label className="field__label" htmlFor="layaway-cash">
            Cash handed over
          </label>
          <input
            id="layaway-cash"
            className="input money"
            inputMode="decimal"
            placeholder={parsed === null ? '0.00' : (parsed / 100).toFixed(2)}
            value={cash}
            onChange={(event) => setCash(sanitiseAmount(event.target.value))}
          />
          <p className="field__hint">
            {shortCash
              ? 'That is less than the deposit.'
              : changeCents > 0
                ? `Change ${money(changeCents)}.`
                : 'Leave blank if they hand over the deposit exactly.'}
          </p>
        </div>
      ) : null}

      {onTerminal && parsed !== null && !tooLittle && !tooMuch && everything ? (
        <TerminalCardPanel
          saleId={sale.sale.id}
          registerId={registerId}
          dueCents={parsed}
          isRefund={false}
          sandbox={cards.sandbox}
          onSaleChanged={() => void depositAttempt.refetch()}
          onCompleted={() => undefined}
        />
      ) : null}
      {depositTaken ? (
        <Callout tone="success">Deposit approved on the card. Put it on layaway to finish.</Callout>
      ) : null}

      {convert.isError ? <Callout tone="danger">{errorMessage(convert.error)}</Callout> : null}
    </Modal>
  );
}
